/**
 * ReportPDF.jsx - PDF Document Component using @react-pdf/renderer
 * Single source of truth for PDF generation
 * Standard Template: Professional layout with natural page flow
 */
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Line as SvgLine,
  Rect,
  Path,
  Circle,
  Link,
} from '@react-pdf/renderer';
import {
  CheckCircleIcon,
  AlertTriangleIcon,
  XCircleIcon,
  TargetIcon,
  AlertCircleIcon,
  StarIcon,
  FlagIcon,
} from './PDFIcons';
import { COLOR_SCHEMES, STYLE_PACKS, STATUS_COLORS, MILESTONE_STATUSES, RISK_LEVELS } from '@/lib/constants';

const DEFAULT_FONT_FAMILY = 'Helvetica';

// ============================================
// APPLE-INSPIRED DESIGN SYSTEM
// ============================================

// Color Palette - Apple/Mac Aesthetic
const APPLE_COLORS = {
  // Base colors
  background: '#FFFFFF',
  cardBackground: '#FAFAFA',
  border: '#E5E5EA',
  borderSubtle: '#F0F0F0',
  
  // Text colors
  textPrimary: '#1D1D1F',
  textSecondary: '#86868B',
  textTertiary: '#AEAEB2',
  
  // Status colors (muted, sophisticated)
  onTrack: '#34C759',
  atRisk: '#FF9500',
  offTrack: '#FF3B30',
  
  // Accent
  accentBlue: '#007AFF',
  
  // Section accent colors (softer)
  accomplishments: '#34C759',
  upcoming: '#007AFF',
  blockers: '#FF3B30',
  risks: '#FF9500',
  decisions: '#AF52DE',
  notes: '#5856D6',
  attachments: '#AF52DE',
  kudos: '#FFD60A',
};

// Typography scale
const TYPOGRAPHY = {
  title: { fontSize: 24, fontWeight: 700, color: APPLE_COLORS.textPrimary, letterSpacing: -0.5 },
  subtitle: { fontSize: 12, fontWeight: 400, color: APPLE_COLORS.textSecondary },
  sectionHeader: { fontSize: 11, fontWeight: 600, color: APPLE_COLORS.textPrimary, textTransform: 'uppercase', letterSpacing: 0.5 },
  body: { fontSize: 10, fontWeight: 400, color: APPLE_COLORS.textPrimary, lineHeight: 1.5 },
  caption: { fontSize: 9, fontWeight: 400, color: APPLE_COLORS.textSecondary },
  metricValue: { fontSize: 24, fontWeight: 700, color: APPLE_COLORS.textPrimary },
  metricLabel: { fontSize: 8, fontWeight: 500, color: APPLE_COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5 },
};

// Month names for date formatting
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// BUG-03 FIX: Parse ISO date string to avoid timezone off-by-one issues
const parseISODate = (dateStr) => {
  if (!dateStr) return null;
  // For YYYY-MM-DD format, parse manually to avoid timezone issues
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

// Format date with flexible format support
// format options: 'M-D-YYYY', 'MMM d, yyyy', 'MMMM d, yyyy'
const formatDate = (dateStr, format = 'M-D-YYYY') => {
  if (!dateStr) return '—';
  const date = parseISODate(dateStr);
  if (!date || isNaN(date)) return dateStr;
  
  const month = date.getMonth();
  const day = date.getDate();
  const year = date.getFullYear();
  
  switch (format) {
    case 'MMMM d, yyyy':
      return `${MONTH_NAMES[month]} ${day}, ${year}`;
    case 'MMM d, yyyy':
      return `${MONTH_ABBREV[month]} ${day}, ${year}`;
    case 'MMM d':
      return `${MONTH_ABBREV[month]} ${day}`;
    default:
      return `${month + 1}-${day}-${year}`;
  }
};

// Create dynamic styles based on color scheme
const createStyles = (scheme) => StyleSheet.create({
  // ============================================
  // PAGE LAYOUT
  // ============================================
  page: {
    padding: 40,
    paddingBottom: 60,
    fontSize: 10,
    fontFamily: DEFAULT_FONT_FAMILY,
    backgroundColor: APPLE_COLORS.background,
  },
  
  // ============================================
  // HEADER - Clean, prominent
  // ============================================
  header: {
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: APPLE_COLORS.border,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerTitleWrap: {
    flex: 1,
    paddingRight: 16,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: APPLE_COLORS.textPrimary,
    letterSpacing: -0.5,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 11,
    color: APPLE_COLORS.textSecondary,
  },
  headerMeta: {
    flexDirection: 'row',
    gap: 32,
    marginTop: 12,
  },
  headerMetaItem: {
    minWidth: 100,
  },
  headerMetaLabel: {
    fontSize: 8,
    color: APPLE_COLORS.textTertiary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  headerMetaValue: {
    fontSize: 10,
    fontWeight: 500,
    color: APPLE_COLORS.textPrimary,
  },
  headerTimeline: {
    marginTop: 12,
  },
  headerTimelineText: {
    fontSize: 9,
    color: APPLE_COLORS.textSecondary,
  },
  
  // ============================================
  // STATUS BADGE - Hero element (pill shape)
  // ============================================
  statusBadge: {
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 16,
    alignSelf: 'flex-start',
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: 700,
    color: '#FFFFFF',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  
  // ============================================
  // STATUS SUMMARY - Blockquote style
  // ============================================
  statusSection: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: APPLE_COLORS.cardBackground,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: APPLE_COLORS.accentBlue,
  },
  statusSummary: {
    fontSize: 11,
    color: APPLE_COLORS.textPrimary,
    lineHeight: 1.6,
    fontStyle: 'italic',
  },
  
  // ============================================
  // SECTIONS
  // ============================================
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: APPLE_COLORS.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: APPLE_COLORS.border,
  },
  
  // ============================================
  // TWO COLUMN LAYOUT
  // ============================================
  twoColumnRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  column: {
    flex: 1,
  },
  
  // ============================================
  // CARDS - Floating card aesthetic
  // ============================================
  columnSection: {
    backgroundColor: APPLE_COLORS.background,
    borderRadius: 8,
    padding: 14,
    minHeight: 90,
    borderWidth: 1,
    borderColor: APPLE_COLORS.border,
    borderBottomWidth: 2,
    borderBottomColor: '#E0E0E0',
  },
  columnSectionTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: APPLE_COLORS.textPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: APPLE_COLORS.border,
  },
  fullWidthSection: {
    backgroundColor: APPLE_COLORS.background,
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: APPLE_COLORS.border,
    borderBottomWidth: 2,
    borderBottomColor: '#E0E0E0',
  },
  
  // ============================================
  // METRICS - Clean cards with large numbers
  // ============================================
  metricsGrid: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  metricBox: {
    flex: 1,
    backgroundColor: APPLE_COLORS.cardBackground,
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: APPLE_COLORS.borderSubtle,
  },
  metricValue: {
    fontSize: 22,
    fontWeight: 700,
    color: APPLE_COLORS.textPrimary,
  },
  metricLabel: {
    fontSize: 8,
    color: APPLE_COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 4,
  },
  
  // ============================================
  // LIST ITEMS - Clean bullets
  // ============================================
  listItem: {
    flexDirection: 'row',
    marginBottom: 6,
    gap: 8,
    alignItems: 'flex-start',
  },
  listBullet: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: APPLE_COLORS.accentBlue,
    marginTop: 4,
  },
  listItemText: {
    flex: 1,
    fontSize: 10,
    color: APPLE_COLORS.textPrimary,
    lineHeight: 1.4,
  },
  listItemMeta: {
    fontSize: 9,
    color: APPLE_COLORS.textSecondary,
  },
  
  // ============================================
  // ALERT/WARNING BOXES
  // ============================================
  alertBox: {
    backgroundColor: '#FFF5F5',
    padding: 10,
    borderRadius: 8,
    marginBottom: 6,
    borderLeftWidth: 3,
    borderLeftColor: APPLE_COLORS.offTrack,
  },
  warningBox: {
    backgroundColor: '#FFFBEB',
    padding: 10,
    borderRadius: 8,
    marginBottom: 6,
    borderLeftWidth: 3,
    borderLeftColor: APPLE_COLORS.atRisk,
  },
  
  // ============================================
  // MILESTONES
  // ============================================
  milestoneRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: APPLE_COLORS.cardBackground,
    borderRadius: 6,
    marginBottom: 4,
    borderWidth: 1,
    borderColor: APPLE_COLORS.borderSubtle,
  },
  milestoneName: {
    fontSize: 10,
    flex: 1,
    color: APPLE_COLORS.textPrimary,
  },
  milestoneDate: {
    fontSize: 9,
    color: APPLE_COLORS.textSecondary,
    marginRight: 8,
  },
  milestoneBadge: {
    fontSize: 8,
    fontWeight: 600,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  
  // ============================================
  // BUDGET
  // ============================================
  budgetGrid: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 8,
  },
  budgetBox: {
    flex: 1,
    padding: 10,
    backgroundColor: APPLE_COLORS.cardBackground,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: APPLE_COLORS.borderSubtle,
  },
  budgetLabel: {
    fontSize: 8,
    color: APPLE_COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 2,
  },
  budgetValue: {
    fontSize: 12,
    fontWeight: 700,
    color: APPLE_COLORS.textPrimary,
  },
  
  // ============================================
  // FOOTER - Clean, no branding
  // ============================================
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 40,
    right: 40,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: APPLE_COLORS.textTertiary,
  },
  
  // ============================================
  // CHARTS
  // ============================================
  chartContainer: {
    height: 140,
    backgroundColor: APPLE_COLORS.cardBackground,
    borderRadius: 8,
    marginTop: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: APPLE_COLORS.borderSubtle,
  },
  
  // ============================================
  // KUDOS
  // ============================================
  kudosItem: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 4,
    alignItems: 'center',
  },
  kudosPerson: {
    fontWeight: 600,
    fontSize: 10,
    color: APPLE_COLORS.textPrimary,
  },
  kudosReason: {
    fontSize: 10,
    color: APPLE_COLORS.textSecondary,
  },
  escalatedBadge: {
    color: '#dc2626',
    backgroundColor: '#fee2e2',
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 2,
    fontSize: 7,
    fontWeight: 700,
  },
});

// Status Badge
// Status Badge - Apple-inspired pill shape (hero element)
const StatusBadge = ({ status }) => {
  const config = {
    'on_track': { label: 'On Track', bg: APPLE_COLORS.onTrack, color: '#FFFFFF' },
    'at_risk': { label: 'At Risk', bg: APPLE_COLORS.atRisk, color: '#FFFFFF' },
    'off_track': { label: 'Off Track', bg: APPLE_COLORS.offTrack, color: '#FFFFFF' },
  };
  const { label, bg, color } = config[status] || config['on_track'];

  return (
    <Text style={{
      paddingVertical: 8,
      paddingHorizontal: 18,
      borderRadius: 16,
      fontSize: 11,
      fontWeight: 700,
      backgroundColor: bg,
      color: color,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}>
      {label}
    </Text>
  );
};

// S-Curve Chart - Updated with Apple colors
const SCurveChart = ({ data, bac }) => {
  if (!data || data.length === 0) return null;

  const width = 450;
  const height = 110;
  const padding = 20;

  const allValues = data.flatMap(d => [d.pv, d.ev, d.ac].filter(v => v != null));
  const maxValue = Math.max(...allValues, bac || 0) || 100;

  const scaleX = (i) => padding + (i / (data.length - 1 || 1)) * (width - 2 * padding);
  const scaleY = (v) => height - padding - ((v || 0) / maxValue) * (height - 2 * padding);

  const createPath = (key) => {
    const points = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(d[key])}`);
    return points.join(' ');
  };

  return (
    <Svg width={width} height={height}>
      <SvgLine x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke={APPLE_COLORS.border} strokeWidth={1} />
      <SvgLine x1={padding} y1={padding} x2={padding} y2={height - padding} stroke={APPLE_COLORS.border} strokeWidth={1} />
      {bac && (
        <SvgLine x1={padding} y1={scaleY(bac)} x2={width - padding} y2={scaleY(bac)} stroke={APPLE_COLORS.textTertiary} strokeWidth={1} strokeDasharray="5,5" />
      )}
      <Path d={createPath('pv')} stroke={APPLE_COLORS.accentBlue} strokeWidth={2} fill="none" />
      <Path d={createPath('ev')} stroke={APPLE_COLORS.onTrack} strokeWidth={2} fill="none" />
      <Path d={createPath('ac')} stroke={APPLE_COLORS.atRisk} strokeWidth={2} fill="none" />
    </Svg>
  );
};

// Header Section - Apple-inspired clean design
const HeaderSection = ({ project, report, reportDate, styles, scheme }) => (
  <View style={styles.header} wrap={false}>
    <View style={styles.headerTop}>
      <View style={styles.headerTitleWrap}>
        <Text style={styles.headerTitle}>{project.name}</Text>
        <Text style={styles.headerSubtitle}>Status Report · {reportDate}</Text>
      </View>
      <StatusBadge status={report.status} />
    </View>
    <View style={styles.headerMeta}>
      <View style={styles.headerMetaItem}>
        <Text style={styles.headerMetaLabel}>Project Manager</Text>
        <Text style={styles.headerMetaValue}>{project.pm_name || '—'}</Text>
      </View>
      <View style={styles.headerMetaItem}>
        <Text style={styles.headerMetaLabel}>Sponsor</Text>
        <Text style={styles.headerMetaValue}>{project.sponsor_name || '—'}</Text>
      </View>
      <View style={styles.headerMetaItem}>
        <Text style={styles.headerMetaLabel}>Report Period</Text>
        <Text style={styles.headerMetaValue}>{formatDate(report.period_start, 'MMM d')} – {formatDate(report.period_end, 'MMM d, yyyy')}</Text>
      </View>
    </View>
  </View>
);

// Timeline Section for PDF - Redesigned with connecting lines
const TimelineSection = ({ project, styles, timelineView = '6_month' }) => {
  // BUG-03 FIX: Use parseISODate to avoid timezone off-by-one
  const projectStart = parseISODate(project.start_date);
  const projectEnd = parseISODate(project.end_date);
  
  // Calculate window based on timelineView prop (days from project start)
  const viewDays = {
    '3_month': 90,
    '6_month': 180,
    '12_month': 365
  };
  const viewLabels = {
    '3_month': '3 Months',
    '6_month': '6 Months',
    '12_month': '12 Months'
  };
  const maxDays = viewDays[timelineView] || 180;
  
  // Window is from TODAY + X days (not project start)
  const today = new Date();
  const windowStart = today > projectStart ? today : projectStart;
  const windowEnd = new Date(windowStart.getTime() + maxDays * 24 * 60 * 60 * 1000);
  const actualWindowEnd = projectEnd < windowEnd ? projectEnd : windowEnd;
  const windowDays = Math.ceil((actualWindowEnd - windowStart) / (1000 * 60 * 60 * 24));
  const totalProjectDays = Math.ceil((projectEnd - projectStart) / (1000 * 60 * 60 * 24));
  const isLongProject = totalProjectDays > maxDays;
  
  // Filter milestones within the window (include completed ones too)
  const baseMilestones = (project.milestones || [])
    .filter(m => {
      // BUG-03 FIX: Use parseISODate
      const mDate = parseISODate(m.target_date);
      return mDate >= windowStart && mDate <= actualWindowEnd;
    })
    .map(m => {
      // BUG-03 FIX: Use parseISODate
      const mDate = parseISODate(m.target_date);
      const daysFromStart = Math.ceil((mDate - windowStart) / (1000 * 60 * 60 * 24));
      const position = windowDays > 0 ? Math.min(Math.max((daysFromStart / windowDays) * 100, 2), 98) : 0;
      return { ...m, daysFromStart, position, date: mDate };
    })
    .sort((a, b) => a.daysFromStart - b.daysFromStart);

  if (baseMilestones.length === 0) return null;

  // Improved collision detection - wider labels for better readability
  const LABEL_WIDTH_PCT = 16; // Width of each label in percentage
  const MIN_GAP_PCT = 2; // Minimum gap between labels
  
  const getLabelBounds = (pct) => ({ 
    left: pct - LABEL_WIDTH_PCT / 2, 
    right: pct + LABEL_WIDTH_PCT / 2,
    center: pct
  });
  
  const overlaps = (b1, b2) => {
    return !(b1.right + MIN_GAP_PCT < b2.left || b1.left - MIN_GAP_PCT > b2.right);
  };

  // Use 4 tiers: 0 (below close), 1 (below far), 2 (above close), 3 (above far)
  const tierOccupancy = [[], [], [], []];
  
  const milestonesWithTiers = baseMilestones.map((milestone, index) => {
    const bounds = getLabelBounds(milestone.position);
    let assignedTier = 0;
    
    // Try to find a tier without collision
    // Alternate between below and above tiers for better distribution
    const tierOrder = index % 2 === 0 ? [0, 2, 1, 3] : [2, 0, 3, 1];
    
    for (const tier of tierOrder) {
      if (!tierOccupancy[tier].some(occupied => overlaps(bounds, occupied))) {
        assignedTier = tier;
        break;
      }
    }
    
    tierOccupancy[assignedTier].push(bounds);
    
    return {
      ...milestone,
      tier: assignedTier,
      isAbove: assignedTier >= 2,
      connectorHeight: (assignedTier === 1 || assignedTier === 3) ? 28 : 14
    };
  });

  // Truncate name more aggressively for dense timelines
  const truncateName = (name) => {
    const maxLen = baseMilestones.length > 5 ? 12 : 16;
    return name?.length > maxLen ? name.substring(0, maxLen) + '…' : name;
  };
  
  const aboveMilestones = milestonesWithTiers.filter(m => m.isAbove);
  const belowMilestones = milestonesWithTiers.filter(m => !m.isAbove);
  
  const aboveHasTall = aboveMilestones.some(m => m.connectorHeight === 28);
  const belowHasTall = belowMilestones.some(m => m.connectorHeight === 28);

  return (
    <View style={[styles.section, { marginBottom: 20 }]} wrap={false}>
      <Text style={{ fontSize: 11, fontWeight: 600, color: APPLE_COLORS.textPrimary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>
        Milestone Timeline {isLongProject ? `(Next ${viewLabels[timelineView]})` : ''}
      </Text>
      
      {/* Above labels + connectors */}
      {aboveMilestones.length > 0 && (
        <View style={{ position: 'relative', height: aboveHasTall ? 58 : 42, marginBottom: 0 }}>
          {aboveMilestones.map((m, idx) => (
            <View
              key={`above-${idx}`}
              style={{
                position: 'absolute',
                left: `${m.position}%`,
                bottom: 0,
                alignItems: 'center',
                marginLeft: -40,
                width: 80,
              }}
            >
              <Text style={{ fontSize: 8, fontWeight: 600, color: m.status === 'complete' ? APPLE_COLORS.onTrack : APPLE_COLORS.textPrimary, textAlign: 'center' }}>
                {truncateName(m.name)}
              </Text>
              <Text style={{ fontSize: 7, color: APPLE_COLORS.textSecondary, textAlign: 'center' }}>
                {formatDate(m.date, 'MMM d')}
              </Text>
              <View style={{ width: 2, height: m.connectorHeight, backgroundColor: m.status === 'complete' ? APPLE_COLORS.onTrack : APPLE_COLORS.textTertiary, marginTop: 2 }} />
            </View>
          ))}
        </View>
      )}

      {/* Date labels - show project start and window end */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
        <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>Start · {formatDate(projectStart, 'MMM d, yyyy')}</Text>
        <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>{formatDate(actualWindowEnd, 'MMM d, yyyy')}</Text>
      </View>
      
      {/* Bar with markers - Apple-style rounded bar */}
      <View style={{ position: 'relative' }}>
        <View style={{ height: 8, backgroundColor: APPLE_COLORS.accentBlue, borderRadius: 4 }} />
        {milestonesWithTiers.map((m, idx) => (
          <View
            key={`marker-${idx}`}
            style={{
              position: 'absolute',
              left: `${m.position}%`,
              top: -3,
              marginLeft: -7,
            }}
          >
            <View style={{
              width: 14,
              height: 14,
              borderRadius: 7,
              backgroundColor: m.status === 'complete' ? APPLE_COLORS.onTrack : 
                             m.status === 'at_risk' ? APPLE_COLORS.atRisk : APPLE_COLORS.accentBlue,
              borderWidth: 2,
              borderColor: APPLE_COLORS.background,
            }} />
          </View>
        ))}
      </View>

      {/* Below connectors + labels */}
      {belowMilestones.length > 0 && (
        <View style={{ position: 'relative', height: belowHasTall ? 58 : 42, marginTop: 0 }}>
          {belowMilestones.map((m, idx) => (
            <View
              key={`below-${idx}`}
              style={{
                position: 'absolute',
                left: `${m.position}%`,
                top: 0,
                alignItems: 'center',
                marginLeft: -40,
                width: 80,
              }}
            >
              <View style={{ width: 2, height: m.connectorHeight, backgroundColor: m.status === 'complete' ? APPLE_COLORS.onTrack : APPLE_COLORS.textTertiary, marginBottom: 2 }} />
              <Text style={{ fontSize: 8, fontWeight: 600, color: m.status === 'complete' ? APPLE_COLORS.onTrack : APPLE_COLORS.textPrimary, textAlign: 'center' }}>
                {truncateName(m.name)}
              </Text>
              <Text style={{ fontSize: 7, color: APPLE_COLORS.textSecondary, textAlign: 'center' }}>
                {formatDate(m.date, 'MMM d')}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

// Status Section
const StatusSection = ({ report, styles }) => {
  if (!report.status_summary) return null;
  return (
    <View style={styles.statusSection} wrap={false}>
      <Text style={styles.statusSummary}>{report.status_summary}</Text>
    </View>
  );
};

// Progress Section
const ProgressSection = ({ report, project, styles, scheme }) => {
  const percent = report.actual_percent ?? null;
  const hasPercent = percent !== null && percent !== undefined;
  const displayPercent = hasPercent ? percent : 0;
  
  // Dynamic color: blue <50%, yellow 50-79%, green 80%+
  const getBarColor = (pct) => {
    if (pct >= 80) return '#22c55e';  // green
    if (pct >= 50) return '#eab308';  // yellow
    return '#3b82f6';  // blue
  };

  return (
    <View style={[styles.section, { marginBottom: 10 }]} wrap={false}>
      <Text style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Progress</Text>
      <View style={{ marginTop: 4 }}>
        {/* Percentage label */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text style={{ fontSize: 9, color: '#64748b' }}>Complete</Text>
          <Text style={{ fontSize: 9, fontWeight: 700, color: hasPercent ? scheme.primary : '#64748b' }}>{hasPercent ? `${displayPercent}%` : '—'}</Text>
        </View>
        {/* Progress bar */}
        <View style={{ height: 12, backgroundColor: '#e2e8f0', borderRadius: 6 }}>
          {hasPercent && (
            <View style={{
              height: 12,
              width: `${displayPercent}%`,
              backgroundColor: getBarColor(displayPercent),
              borderRadius: 6
            }} />
          )}
        </View>
      </View>
    </View>
  );
};

// Metrics Section - Apple-style cards with large numbers
const MetricsSection = ({ report, project, styles, scheme, riskFilter = 'active' }) => {
  const actualPct = report.actual_percent ?? null;
  const hasActualPct = actualPct !== null && actualPct !== undefined;
  const displayPct = hasActualPct ? actualPct : 0;
  const totalMilestones = project.milestones?.length || 0;
  const completedMilestones = project.milestones?.filter(m => m.status === 'complete').length || 0;
  const getFilteredRiskCount = () => {
    const risks = project.risks || [];
    if (riskFilter === 'active') return risks.filter(r => r.status === 'open' || r.status === 'mitigated').length;
    return risks.length;
  };
  const activeRisks = getFilteredRiskCount();
  const activeBlockers = report.blockers?.filter(b => !b.solved).length || 0;
  const hasEvm = project.evm_enabled && report.evm;

  // Helper function for metric card colors (softer Apple palette)
  const getMetricStyle = (isGood, isWarning = false) => ({
    bg: isGood ? '#E8F8ED' : isWarning ? '#FEF7E8' : '#FEF2F2',
    color: isGood ? APPLE_COLORS.onTrack : isWarning ? APPLE_COLORS.atRisk : APPLE_COLORS.offTrack,
  });

  const pctStyle = hasActualPct ? getMetricStyle(displayPct >= 80, displayPct >= 50 && displayPct < 80) : { bg: '#f1f5f9', color: '#64748b' };

  return (
    <View style={styles.section} wrap={false}>
      <Text style={styles.sectionTitle}>Key Metrics</Text>
      <View style={styles.metricsGrid}>
        <View style={[styles.metricBox, { backgroundColor: pctStyle.bg }]}>
          <Text style={[styles.metricValue, { color: pctStyle.color }]}>{hasActualPct ? `${displayPct}%` : '—'}</Text>
          <Text style={styles.metricLabel}>Complete</Text>
        </View>
        {hasEvm && report.evm?.spi && (
          <View style={[styles.metricBox, { backgroundColor: getMetricStyle(report.evm.spi >= 0.95, report.evm.spi >= 0.9).bg }]}>
            <Text style={[styles.metricValue, { color: getMetricStyle(report.evm.spi >= 0.95, report.evm.spi >= 0.9).color }]}>
              {report.evm.spi.toFixed(2)}
            </Text>
            <Text style={styles.metricLabel}>SPI</Text>
          </View>
        )}
        {hasEvm && report.evm?.cpi && (
          <View style={[styles.metricBox, { backgroundColor: getMetricStyle(report.evm.cpi >= 0.95, report.evm.cpi >= 0.9).bg }]}>
            <Text style={[styles.metricValue, { color: getMetricStyle(report.evm.cpi >= 0.95, report.evm.cpi >= 0.9).color }]}>
              {report.evm.cpi.toFixed(2)}
            </Text>
            <Text style={styles.metricLabel}>CPI</Text>
          </View>
        )}
        <View style={[styles.metricBox, { backgroundColor: completedMilestones === totalMilestones && totalMilestones > 0 ? '#E8F8ED' : APPLE_COLORS.cardBackground }]}>
          <Text style={[styles.metricValue, { color: APPLE_COLORS.onTrack }]}>{completedMilestones}/{totalMilestones}</Text>
          <Text style={styles.metricLabel}>Milestones</Text>
        </View>
        <View style={[styles.metricBox, { backgroundColor: getMetricStyle(activeRisks === 0, activeRisks <= 2).bg }]}>
          <Text style={[styles.metricValue, { color: getMetricStyle(activeRisks === 0, activeRisks <= 2).color }]}>{activeRisks}</Text>
          <Text style={styles.metricLabel}>Active Risks</Text>
        </View>
        <View style={[styles.metricBox, { backgroundColor: getMetricStyle(activeBlockers === 0).bg }]}>
          <Text style={[styles.metricValue, { color: activeBlockers === 0 ? APPLE_COLORS.onTrack : APPLE_COLORS.offTrack }]}>{activeBlockers}</Text>
          <Text style={styles.metricLabel}>Blockers</Text>
        </View>
      </View>
    </View>
  );
};

// Chart Section - Updated with Apple colors
const ChartSection = ({ chartData, project, styles }) => {
  // Show placeholder when no chart data or insufficient data points
  if (!chartData?.scurve_data?.length || chartData.scurve_data.length < 2) {
    return (
      <View style={styles.section} wrap={false}>
        <Text style={styles.sectionTitle}>Project Performance Timeline</Text>
        <View style={{ backgroundColor: '#f8fafc', borderRadius: 6, padding: 16, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: '#64748b', textAlign: 'center' }}>
            {chartData?.scurve_data?.length === 1 
              ? 'Performance chart will appear after your second report with EVM data.'
              : 'Performance timeline will appear after reports with EVM data (PV, EV, AC) are created.'}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.section} wrap={false}>
      <Text style={styles.sectionTitle}>Project Performance Timeline</Text>
      <View style={styles.chartContainer}>
        <SCurveChart data={chartData.scurve_data} bac={project.bac} />
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 16, marginTop: 8 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 3, backgroundColor: APPLE_COLORS.accentBlue, borderRadius: 1 }} />
            <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>Planned (PV)</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 3, backgroundColor: APPLE_COLORS.onTrack, borderRadius: 1 }} />
            <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>Earned (EV)</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 3, backgroundColor: APPLE_COLORS.atRisk, borderRadius: 1 }} />
            <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>Actual (AC)</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

// Accomplishments Section - Apple-style with clean bullets
const AccomplishmentsSection = ({ report, styles, inColumn = false }) => {
  if (!report.accomplishments?.length) return null;

  const sectionStyle = inColumn
    ? [styles.columnSection, { borderLeftWidth: 0, borderWidth: 1, borderColor: APPLE_COLORS.border, borderBottomWidth: 2, borderBottomColor: '#E0E0E0' }]
    : [styles.fullWidthSection, { borderLeftWidth: 0, borderWidth: 1, borderColor: APPLE_COLORS.border, borderBottomWidth: 2, borderBottomColor: '#E0E0E0' }];

  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={[styles.columnSectionTitle, { color: APPLE_COLORS.onTrack }]}>Accomplishments ({report.accomplishments.length})</Text>
      {report.accomplishments.slice(0, inColumn ? 5 : 10).map((item, i) => (
        <View key={i} style={styles.listItem}>
          <View style={[styles.listBullet, { backgroundColor: APPLE_COLORS.onTrack }]} />
          <View style={{ flex: 1 }}>
            <Text style={styles.listItemText}>{item.description}</Text>
            {item.owner && <Text style={styles.listItemMeta}>{item.owner}</Text>}
          </View>
        </View>
      ))}
    </View>
  );
};

// Upcoming Section - Apple-style
const UpcomingSection = ({ report, styles, scheme, inColumn = false }) => {
  if (!report.upcoming?.length) return null;

  const sectionStyle = inColumn
    ? [styles.columnSection, { borderLeftWidth: 0, borderWidth: 1, borderColor: APPLE_COLORS.border, borderBottomWidth: 2, borderBottomColor: '#E0E0E0' }]
    : [styles.fullWidthSection, { borderLeftWidth: 0, borderWidth: 1, borderColor: APPLE_COLORS.border, borderBottomWidth: 2, borderBottomColor: '#E0E0E0' }];

  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={[styles.columnSectionTitle, { color: APPLE_COLORS.accentBlue }]}>Upcoming Work ({report.upcoming.length})</Text>
      {report.upcoming.slice(0, inColumn ? 5 : 10).map((item, i) => (
        <View key={i} style={styles.listItem}>
          <View style={[styles.listBullet, { backgroundColor: APPLE_COLORS.accentBlue }]} />
          <View style={{ flex: 1 }}>
            <Text style={styles.listItemText}>{item.description}</Text>
            {item.due_date && <Text style={styles.listItemMeta}>Due: {formatDate(item.due_date, 'MMM d, yyyy')}</Text>}
          </View>
        </View>
      ))}
    </View>
  );
};

// Blockers Section - Apple-style with subtle red accent
const BlockersSection = ({ report, styles, inColumn = false }) => {
  if (!report.blockers?.length) return null;
  
  const sectionStyle = inColumn 
    ? [styles.columnSection, { borderLeftWidth: 3, borderLeftColor: APPLE_COLORS.offTrack, backgroundColor: '#FFF5F5' }]
    : [styles.fullWidthSection, { borderLeftWidth: 3, borderLeftColor: APPLE_COLORS.offTrack, backgroundColor: '#FFF5F5' }];
  
  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={[styles.columnSectionTitle, { color: APPLE_COLORS.offTrack }]}>Blockers ({report.blockers.length})</Text>
      {report.blockers.slice(0, inColumn ? 3 : 6).map((item, i) => (
        <View key={i} style={{ marginBottom: 10, flexDirection: 'column' }} wrap={false}>
          <Text style={{ fontSize: 10, color: APPLE_COLORS.textPrimary, fontWeight: 600, lineHeight: 1.4 }}>
            {item.description}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 8, flexWrap: 'wrap' }}>
            {item.owner && (
              <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>Owner: {item.owner}</Text>
            )}
            {item.days_blocked && (
              <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>{item.days_blocked}d blocked</Text>
            )}
            {item.target_resolution_date && (
              <Text style={{ fontSize: 8, color: APPLE_COLORS.textSecondary }}>Target: {formatDate(item.target_resolution_date, 'MMM d')}</Text>
            )}
            {item.escalation_date && (
              <Text style={{ fontSize: 8, color: APPLE_COLORS.offTrack }}>Escalated: {formatDate(item.escalation_date, 'MMM d')}</Text>
            )}
            {item.escalated && (
              <Text style={{ fontSize: 7, color: '#FFFFFF', fontWeight: 700, backgroundColor: APPLE_COLORS.offTrack, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 }}>
                ESCALATED
              </Text>
            )}
          </View>
        </View>
      ))}
    </View>
  );
};

// Risks Section - Apple-style
const RisksSection = ({ project, styles, scheme, inColumn = false, riskFilter = 'active' }) => {
  let filteredRisks = project.risks || [];
  
  if (riskFilter === 'active') {
    filteredRisks = filteredRisks.filter(r => r.status === 'open' || r.status === 'mitigated');
  }
  
  if (!filteredRisks.length) return null;
  
  const sectionStyle = inColumn 
    ? [styles.columnSection, { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb' }]
    : [styles.fullWidthSection, { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb' }];
  
  const titleStyle = [styles.columnSectionTitle, { color: '#b45309', borderBottomColor: '#f59e0b' }];
  
  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={titleStyle}>Active Risks ({filteredRisks.length})</Text>
      {filteredRisks.slice(0, inColumn ? 3 : 6).map((risk, i) => {
        const probInfo = RISK_LEVELS.find(l => l.value === risk.probability);
        const impactInfo = RISK_LEVELS.find(l => l.value === risk.impact);
        const probLabel = probInfo?.label?.charAt(0) || risk.probability;
        const impactLabel = impactInfo?.label?.charAt(0) || risk.impact;
        
        return (
          <View key={i} style={{ marginBottom: 10, flexDirection: 'column' }} wrap={false}>
            <Text style={{ fontSize: 9, color: '#334155', fontWeight: 600, lineHeight: 1.3 }}>
              {risk.description}
            </Text>
            <Text style={{ fontSize: 7, color: '#92400e', marginTop: 4 }}>
              Probability: {probLabel} | Impact: {impactLabel}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// Notes Section
const NotesSection = ({ report, styles, inColumn = false }) => {
  if (!report.notes) return null;

  const sectionStyle = inColumn
    ? [styles.columnSection, { borderLeftColor: '#6366f1' }]
    : [styles.fullWidthSection, { borderLeftColor: '#6366f1' }];

  const titleStyle = [styles.columnSectionTitle, { color: '#4f46e5', borderBottomColor: '#6366f1' }];

  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={titleStyle}>Notes</Text>
      <Text style={styles.listItemText}>{report.notes}</Text>
    </View>
  );
};

// Attachments Section
const AttachmentsSection = ({ report, styles, inColumn = false }) => {
  const attachments = report.attachments || [];
  if (!attachments.length) return null;

  const sectionStyle = inColumn
    ? [styles.columnSection, { borderLeftColor: '#8b5cf6' }]
    : [styles.fullWidthSection, { borderLeftColor: '#8b5cf6' }];

  const titleStyle = [styles.columnSectionTitle, { color: '#7c3aed', borderBottomColor: '#8b5cf6' }];

  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={titleStyle}>Links</Text>
      {attachments.map((attachment, index) => (
        <View key={attachment.id || index} style={{ marginBottom: 4, flexDirection: 'row', flexWrap: 'wrap' }}>
          <Text style={styles.listItemText}>
            • {attachment.label}:{' '}
          </Text>
          <Link src={attachment.url} style={{ fontSize: 9, color: '#3b82f6' }}>
            {attachment.url}
          </Link>
        </View>
      ))}
    </View>
  );
};

// Kudos Section
const KudosSection = ({ report, styles, scheme, inColumn = false }) => {
  const kudosArray = Array.isArray(report.kudos) ? report.kudos : [];
  const kudosString = typeof report.kudos === 'string' ? report.kudos : '';

  if (!kudosArray.length && !kudosString.trim()) return null;

  const sectionStyle = inColumn
    ? [styles.columnSection, { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb' }]
    : [styles.fullWidthSection, { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb' }];

  const titleStyle = [styles.columnSectionTitle, { color: '#b45309', borderBottomColor: '#f59e0b' }];

  return (
    <View style={sectionStyle} wrap={false}>
      <Text style={titleStyle}>Kudos</Text>
      {kudosString.trim() ? (
        <Text style={styles.listItemText}>{kudosString}</Text>
      ) : (
        kudosArray.filter(k => k && (k.person || k.reason)).slice(0, inColumn ? 4 : 8).map((k, i) => (
          <View key={i} style={styles.kudosItem}>
            <StarIcon size={9} color="#f59e0b" />
            <Text style={styles.kudosPerson}>{k.person}</Text>
            {k.reason && <Text style={styles.kudosReason}>- {k.reason}</Text>}
          </View>
        ))
      )}
    </View>
  );
};

// Custom Sections Component
const CustomSectionsComponent = ({ customSections, styles, scheme }) => {
  if (!customSections || customSections.length === 0) return null;
  
  return (
    <>
      {customSections.filter(s => s.name || s.content).map((section, index) => (
        <View key={section.id || index} style={styles.section} wrap={false}>
          <Text style={styles.sectionTitle}>
            {section.name || `Custom Section ${index + 1}`}
          </Text>
          {section.content && (
            <Text style={styles.listItemText}>{section.content}</Text>
          )}
        </View>
      ))}
    </>
  );
};

// Milestones Section
const MilestonesSection = ({ project, styles, scheme }) => {
  if (!project.milestones?.length) return null;

  return (
    <View style={styles.section} wrap={false}>
      <Text style={styles.sectionTitle}>Milestones ({project.milestones.length})</Text>
      {project.milestones.slice(0, 6).map((m, i) => {
        const statusInfo = MILESTONE_STATUSES.find(s => s.value === m.status);
        const bgColor = m.status === 'complete' ? '#dcfce7' : m.status === 'at_risk' ? '#fef3c7' : '#f1f5f9';
        const textColor = m.status === 'complete' ? '#166534' : m.status === 'at_risk' ? '#92400e' : '#475569';

        return (
          <View key={i} style={styles.milestoneRow} wrap={false}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, flex: 1 }}>
              {m.status === 'complete' ? (
                <CheckCircleIcon size={9} color="#16a34a" />
              ) : (
                <FlagIcon size={9} color="#64748b" />
              )}
              <Text style={styles.milestoneName}>{m.name}</Text>
            </View>
            <Text style={styles.milestoneDate}>{formatDate(m.target_date)}</Text>
            <Text style={[styles.milestoneBadge, { backgroundColor: bgColor, color: textColor }]}>
              {statusInfo?.label || m.status}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// Budget Section
const BudgetSection = ({ report, project, styles }) => {
  if (!project.bac || !report.pv) return null;
  const hasEvm = project.evm_enabled && report.evm;

  return (
    <View style={styles.section} wrap={false}>
      <Text style={styles.sectionTitle}>Budget Summary</Text>
      <View style={styles.budgetGrid}>
        <View style={styles.budgetBox}>
          <Text style={styles.budgetLabel}>PV (Planned)</Text>
          <Text style={styles.budgetValue}>${report.pv?.toLocaleString()}</Text>
        </View>
        <View style={styles.budgetBox}>
          <Text style={styles.budgetLabel}>EV (Earned)</Text>
          <Text style={styles.budgetValue}>${report.ev?.toLocaleString()}</Text>
        </View>
        <View style={styles.budgetBox}>
          <Text style={styles.budgetLabel}>AC (Actual)</Text>
          <Text style={styles.budgetValue}>${report.ac?.toLocaleString()}</Text>
        </View>
      </View>
      {hasEvm && report.evm?.eac && (
        <View style={[styles.budgetGrid, { marginTop: 6 }]}>
          <View style={styles.budgetBox}>
            <Text style={styles.budgetLabel}>BAC</Text>
            <Text style={styles.budgetValue}>${project.bac?.toLocaleString()}</Text>
          </View>
          <View style={styles.budgetBox}>
            <Text style={styles.budgetLabel}>EAC</Text>
            <Text style={styles.budgetValue}>${report.evm.eac?.toLocaleString()}</Text>
          </View>
          <View style={[styles.budgetBox, { backgroundColor: report.evm.vac >= 0 ? '#dcfce7' : '#fee2e2' }]}>
            <Text style={styles.budgetLabel}>VAC</Text>
            <Text style={[styles.budgetValue, { color: report.evm.vac >= 0 ? '#16a34a' : '#dc2626' }]}>
              {report.evm.vac >= 0 ? '+' : ''}${report.evm.vac?.toLocaleString()}
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

// Scope Section
const ScopeSection = ({ project, styles }) => {
  if (!project.scope_summary) return null;

  return (
    <View style={[styles.section, { marginBottom: 12 }]} wrap={false}>
      <Text style={styles.sectionTitle}>SCOPE</Text>
      <Text style={styles.listItemText}>{project.scope_summary}</Text>
    </View>
  );
};

// Decisions Section
const DecisionsSection = ({ report, styles }) => {
  if (!report.decisions?.length) return null;

  return (
    <View style={styles.section} wrap={false}>
      <Text style={[styles.sectionTitle, { color: '#f59e0b' }]}>Decisions Needed ({report.decisions.length})</Text>
      {report.decisions.map((item, i) => (
        <View key={i} style={styles.warningBox}>
          <Text style={[styles.listItemText, { fontWeight: 600 }]}>{item.description}</Text>
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 3 }}>
            {item.needed_by && <Text style={styles.listItemMeta}>Needed by: {formatDate(item.needed_by)}</Text>}
            {item.recommendation && <Text style={styles.listItemMeta}>Rec: {item.recommendation}</Text>}
          </View>
        </View>
      ))}
    </View>
  );
};

// Executive Template Components
const ExecutiveHeaderSection = ({ project, report, reportDate, styles, scheme }) => (
  <View style={[styles.header, { backgroundColor: scheme.primary, padding: 10, marginBottom: 8 }]}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 16, fontWeight: 700, color: '#ffffff' }}>{project.name}</Text>
        <Text style={{ fontSize: 9, color: '#ffffff', opacity: 0.85, marginTop: 2 }}>Executive Summary • {reportDate}</Text>
      </View>
      <StatusBadge status={report.status} />
    </View>
    <View style={{ flexDirection: 'row', gap: 16, marginTop: 8, paddingTop: 6, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.2)' }}>
      <View>
        <Text style={{ fontSize: 7, color: '#ffffff', opacity: 0.7 }}>PM</Text>
        <Text style={{ fontSize: 8, color: '#ffffff', fontWeight: 600 }}>{project.pm_name || '—'}</Text>
      </View>
      <View>
        <Text style={{ fontSize: 7, color: '#ffffff', opacity: 0.7 }}>Sponsor</Text>
        <Text style={{ fontSize: 8, color: '#ffffff', fontWeight: 600 }}>{project.sponsor_name || '—'}</Text>
      </View>
      <View>
        <Text style={{ fontSize: 7, color: '#ffffff', opacity: 0.7 }}>Period</Text>
        <Text style={{ fontSize: 8, color: '#ffffff', fontWeight: 600 }}>{formatDate(report.period_start)} - {formatDate(report.period_end)}</Text>
      </View>
    </View>
  </View>
);

const ExecutiveMetricsSection = ({ report, project, styles, scheme, riskFilter }) => {
  const actualPct = report.actual_percent ?? null;
  const hasActualPct = actualPct !== null && actualPct !== undefined;
  const hasEvm = project.evm_enabled && report.evm;
  const getFilteredRiskCount = () => {
    const risks = project.risks || [];
    if (riskFilter === 'active') return risks.filter(r => r.status === 'open' || r.status === 'mitigated').length;
    return risks.length;
  };
  const activeRisks = getFilteredRiskCount();
  const activeBlockers = report.blockers?.filter(b => !b.solved).length || 0;

  return (
    <View style={{ flexDirection: 'row', gap: 6, marginBottom: 8 }}>
      <View style={{ flex: 1, backgroundColor: hasActualPct ? (actualPct >= 80 ? '#dcfce7' : actualPct >= 50 ? '#fef3c7' : '#fee2e2') : '#f1f5f9', padding: 6, borderRadius: 3, alignItems: 'center' }}>
        <Text style={{ fontSize: 14, fontWeight: 700, color: hasActualPct ? (actualPct >= 80 ? '#16a34a' : actualPct >= 50 ? '#ca8a04' : '#dc2626') : '#64748b' }}>{hasActualPct ? `${actualPct}%` : '—'}</Text>
        <Text style={{ fontSize: 6, color: '#64748b', textTransform: 'uppercase' }}>Complete</Text>
      </View>
      {hasEvm && report.evm?.spi && (
        <View style={{ flex: 1, backgroundColor: report.evm.spi >= 0.95 ? '#dcfce7' : report.evm.spi >= 0.9 ? '#fef3c7' : '#fee2e2', padding: 6, borderRadius: 3, alignItems: 'center' }}>
          <Text style={{ fontSize: 14, fontWeight: 700, color: report.evm.spi >= 0.95 ? '#16a34a' : report.evm.spi >= 0.9 ? '#ca8a04' : '#dc2626' }}>{report.evm.spi.toFixed(2)}</Text>
          <Text style={{ fontSize: 6, color: '#64748b', textTransform: 'uppercase' }}>SPI</Text>
        </View>
      )}
      {hasEvm && report.evm?.cpi && (
        <View style={{ flex: 1, backgroundColor: report.evm.cpi >= 0.95 ? '#dcfce7' : report.evm.cpi >= 0.9 ? '#fef3c7' : '#fee2e2', padding: 6, borderRadius: 3, alignItems: 'center' }}>
          <Text style={{ fontSize: 14, fontWeight: 700, color: report.evm.cpi >= 0.95 ? '#16a34a' : report.evm.cpi >= 0.9 ? '#ca8a04' : '#dc2626' }}>{report.evm.cpi.toFixed(2)}</Text>
          <Text style={{ fontSize: 6, color: '#64748b', textTransform: 'uppercase' }}>CPI</Text>
        </View>
      )}
      <View style={{ flex: 1, backgroundColor: activeRisks === 0 ? '#dcfce7' : activeRisks <= 2 ? '#fef3c7' : '#fee2e2', padding: 6, borderRadius: 3, alignItems: 'center' }}>
        <Text style={{ fontSize: 14, fontWeight: 700, color: activeRisks === 0 ? '#16a34a' : activeRisks <= 2 ? '#ca8a04' : '#dc2626' }}>{activeRisks}</Text>
        <Text style={{ fontSize: 6, color: '#64748b', textTransform: 'uppercase' }}>Risks</Text>
      </View>
      <View style={{ flex: 1, backgroundColor: activeBlockers === 0 ? '#dcfce7' : '#fee2e2', padding: 6, borderRadius: 3, alignItems: 'center' }}>
        <Text style={{ fontSize: 14, fontWeight: 700, color: activeBlockers === 0 ? '#16a34a' : '#dc2626' }}>{activeBlockers}</Text>
        <Text style={{ fontSize: 6, color: '#64748b', textTransform: 'uppercase' }}>Blockers</Text>
      </View>
    </View>
  );
};

const ExecutiveAccomplishmentsSection = ({ report, styles }) => {
  if (!report.accomplishments?.length) return null;
  const topItems = report.accomplishments.slice(0, 3);
  
  return (
    <View style={{ backgroundColor: '#f0fdf4', padding: 8, borderRadius: 3, borderLeftWidth: 2, borderLeftColor: '#22c55e', marginBottom: 6 }}>
      <Text style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>Key Accomplishments</Text>
      {topItems.map((item, i) => (
        <View key={i} style={{ flexDirection: 'row', marginBottom: 2, alignItems: 'flex-start', gap: 4 }}>
          <Text style={{ fontSize: 8, color: '#16a34a' }}>•</Text>
          <Text style={{ fontSize: 8, color: '#334155', flex: 1 }}>{item.description}</Text>
        </View>
      ))}
    </View>
  );
};

const ExecutiveIssuesSection = ({ report, project, styles, riskFilter }) => {
  const blockers = report.blockers?.filter(b => !b.solved)?.slice(0, 2) || [];
  let filteredRisks = project.risks || [];
  if (riskFilter === 'active') {
    filteredRisks = filteredRisks.filter(r => r.status === 'open' || r.status === 'mitigated');
  }
  const risks = filteredRisks.slice(0, 2);
  
  if (!blockers.length && !risks.length) return null;
  
  return (
    <View style={{ flexDirection: 'row', gap: 6, marginBottom: 6 }}>
      {blockers.length > 0 && (
        <View style={{ flex: 1, backgroundColor: '#fef2f2', padding: 8, borderRadius: 3, borderLeftWidth: 2, borderLeftColor: '#dc2626' }}>
          <Text style={{ fontSize: 9, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>Blockers</Text>
          {blockers.map((item, i) => (
            <View key={i} style={{ marginBottom: 3 }}>
              <Text style={{ fontSize: 8, color: '#334155' }}>{item.description}</Text>
              {item.escalated && <Text style={{ fontSize: 6, color: '#dc2626', fontWeight: 700 }}>ESCALATED</Text>}
            </View>
          ))}
        </View>
      )}
      {risks.length > 0 && (
        <View style={{ flex: 1, backgroundColor: '#fffbeb', padding: 8, borderRadius: 3, borderLeftWidth: 2, borderLeftColor: '#f59e0b' }}>
          <Text style={{ fontSize: 9, fontWeight: 700, color: '#b45309', marginBottom: 4 }}>Top Risks</Text>
          {risks.map((risk, i) => (
            <View key={i} style={{ marginBottom: 3 }}>
              <Text style={{ fontSize: 8, color: '#334155' }}>{risk.description}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const ExecutiveDecisionSection = ({ report, styles }) => {
  if (!report.decisions?.length) return null;
  const topDecisions = report.decisions.slice(0, 2);
  
  return (
    <View style={{ backgroundColor: '#fef3c7', padding: 8, borderRadius: 3, borderLeftWidth: 2, borderLeftColor: '#f59e0b', marginBottom: 6 }}>
      <Text style={{ fontSize: 9, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Decisions Needed</Text>
      {topDecisions.map((item, i) => (
        <View key={i} style={{ marginBottom: 3 }}>
          <Text style={{ fontSize: 8, color: '#334155', fontWeight: 600 }}>{item.description}</Text>
          {item.needed_by && <Text style={{ fontSize: 7, color: '#64748b' }}>Needed by: {formatDate(item.needed_by)}</Text>}
        </View>
      ))}
    </View>
  );
};

// Compact Template Components
const CompactHeaderSection = ({ project, report, reportDate, styles, scheme }) => (
  <View style={[styles.header, { backgroundColor: scheme.primary, padding: 8, marginBottom: 6 }]}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
      <View style={{ flex: 1, marginRight: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
          <Text style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>{project.name}</Text>
          <Text style={{ fontSize: 8, color: '#ffffff', opacity: 0.7 }}>|</Text>
          <Text style={{ fontSize: 8, color: '#ffffff', opacity: 0.9 }}>{reportDate}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 3 }}>
          <Text style={{ fontSize: 7, color: '#ffffff', opacity: 0.8 }}>PM: {project.pm_name || '—'}</Text>
          <Text style={{ fontSize: 7, color: '#ffffff', opacity: 0.6 }}>|</Text>
          <Text style={{ fontSize: 7, color: '#ffffff', opacity: 0.8 }}>Sponsor: {project.sponsor_name || '—'}</Text>
        </View>
        {report.status_summary && (
          <View style={{ marginTop: 4, paddingTop: 4, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.2)' }}>
            <Text style={{ fontSize: 8, color: '#ffffff', opacity: 0.95, fontStyle: 'italic' }}>
              {report.status_summary}
            </Text>
          </View>
        )}
      </View>
      <StatusBadge status={report.status} />
    </View>
  </View>
);

const CompactMetricsSection = ({ report, project, riskFilter = 'active' }) => {
  const actualPct = report.actual_percent ?? null;
  const hasActualPct = actualPct !== null && actualPct !== undefined;
  const completedMilestones = project.milestones?.filter(m => m.status === 'complete').length || 0;
  const totalMilestones = project.milestones?.length || 0;
  
  const getFilteredRiskCount = () => {
    const risks = project.risks || [];
    if (riskFilter === 'active') return risks.filter(r => r.status === 'open' || r.status === 'mitigated').length;
    return risks.length;
  };
  const activeRisks = getFilteredRiskCount();
  const activeBlockers = report.blockers?.length || 0;
  
  const boxStyle = { 
    flex: 1, 
    backgroundColor: '#f8fafc', 
    padding: 4, 
    borderRadius: 3, 
    alignItems: 'center' 
  };
  const valueStyle = { fontSize: 10, fontWeight: 700 };
  const labelStyle = { fontSize: 6, color: '#64748b', textTransform: 'uppercase' };
  
  return (
    <View style={{ flexDirection: 'row', gap: 4, marginBottom: 6 }}>
      <View style={[boxStyle, { backgroundColor: hasActualPct ? (actualPct >= 80 ? '#dcfce7' : actualPct >= 50 ? '#fef3c7' : '#fee2e2') : '#f1f5f9' }]}>
        <Text style={[valueStyle, { color: hasActualPct ? (actualPct >= 80 ? '#16a34a' : actualPct >= 50 ? '#ca8a04' : '#dc2626') : '#64748b' }]}>{hasActualPct ? `${actualPct}%` : '—'}</Text>
        <Text style={labelStyle}>DONE</Text>
      </View>
      {report.evm?.spi && (
        <View style={[boxStyle, { backgroundColor: report.evm.spi >= 0.95 ? '#dcfce7' : report.evm.spi >= 0.9 ? '#fef3c7' : '#fee2e2' }]}>
          <Text style={[valueStyle, { color: report.evm.spi >= 0.95 ? '#16a34a' : report.evm.spi >= 0.9 ? '#ca8a04' : '#dc2626' }]}>{report.evm.spi.toFixed(2)}</Text>
          <Text style={labelStyle}>SPI</Text>
        </View>
      )}
      {report.evm?.cpi && (
        <View style={[boxStyle, { backgroundColor: report.evm.cpi >= 0.95 ? '#dcfce7' : report.evm.cpi >= 0.9 ? '#fef3c7' : '#fee2e2' }]}>
          <Text style={[valueStyle, { color: report.evm.cpi >= 0.95 ? '#16a34a' : report.evm.cpi >= 0.9 ? '#ca8a04' : '#dc2626' }]}>{report.evm.cpi.toFixed(2)}</Text>
          <Text style={labelStyle}>CPI</Text>
        </View>
      )}
      <View style={boxStyle}>
        <Text style={[valueStyle, { color: '#16a34a' }]}>{completedMilestones}/{totalMilestones}</Text>
        <Text style={labelStyle}>MILES.</Text>
      </View>
      <View style={[boxStyle, { backgroundColor: activeRisks === 0 ? '#dcfce7' : activeRisks <= 2 ? '#fef3c7' : '#fee2e2' }]}>
        <Text style={[valueStyle, { color: activeRisks === 0 ? '#16a34a' : activeRisks <= 2 ? '#ca8a04' : '#dc2626' }]}>{activeRisks}</Text>
        <Text style={labelStyle}>RISKS</Text>
      </View>
      <View style={[boxStyle, { backgroundColor: activeBlockers === 0 ? '#dcfce7' : '#fee2e2' }]}>
        <Text style={[valueStyle, { color: activeBlockers === 0 ? '#16a34a' : '#dc2626' }]}>{activeBlockers}</Text>
        <Text style={labelStyle}>BLK.</Text>
      </View>
    </View>
  );
};

const CompactChartSection = ({ chartData, project, styles }) => {
  if (!chartData?.scurve_data?.length || chartData.scurve_data.length < 2) {
    return (
      <View style={{ marginBottom: 6 }} wrap={false}>
        <Text style={{ fontSize: 7, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', color: '#64748b' }}>PERFORMANCE</Text>
        <View style={{ backgroundColor: '#f8fafc', borderRadius: 3, padding: 12, alignItems: 'center' }}>
          <Text style={{ fontSize: 7, color: '#64748b', textAlign: 'center' }}>
            {chartData?.scurve_data?.length === 1 
              ? 'Performance chart will appear after your second report with EVM data.'
              : 'Performance chart will appear after reports with EVM data are created.'}
          </Text>
        </View>
      </View>
    );
  }
  return (
    <View style={{ marginBottom: 6 }} wrap={false}>
      <Text style={{ fontSize: 7, fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', color: '#64748b' }}>PERFORMANCE</Text>
      <View style={{ height: 160, backgroundColor: '#f8fafc', borderRadius: 3, padding: 8, alignItems: 'center' }}>
        <SCurveChart data={chartData.scurve_data} bac={project?.bac} />
        <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 14, marginTop: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
            <View style={{ width: 10, height: 3, backgroundColor: '#3b82f6' }} />
            <Text style={{ fontSize: 7, color: '#64748b' }}>Planned (PV)</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
            <View style={{ width: 10, height: 3, backgroundColor: '#22c55e' }} />
            <Text style={{ fontSize: 7, color: '#64748b' }}>Earned (EV)</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
            <View style={{ width: 10, height: 3, backgroundColor: '#f97316' }} />
            <Text style={{ fontSize: 7, color: '#64748b' }}>Actual (AC)</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

// Visual Template Components
const VisualHeaderSection = ({ project, report, reportDate, styles, scheme }) => {
  const statusConfig = {
    'on_track': { label: 'ON TRACK', bg: '#22c55e', color: '#ffffff' },
    'at_risk': { label: 'AT RISK', bg: '#f59e0b', color: '#ffffff' },
    'off_track': { label: 'OFF TRACK', bg: '#dc2626', color: '#ffffff' },
  };
  const { label, bg, color } = statusConfig[report.status] || statusConfig['on_track'];

  return (
    <View style={{ marginBottom: 16 }}>
      {/* Status Banner */}
      <View style={{ backgroundColor: bg, padding: 12, borderRadius: 6, marginBottom: 12, alignItems: 'center' }}>
        <Text style={{ fontSize: 24, fontWeight: 700, color: color, letterSpacing: 2 }}>{label}</Text>
      </View>
      
      {/* Project Info */}
      <View style={{ backgroundColor: scheme.primary, padding: 14, borderRadius: 6 }}>
        <Text style={{ fontSize: 20, fontWeight: 700, color: '#ffffff', marginBottom: 4 }}>{project.name}</Text>
        <Text style={{ fontSize: 11, color: '#ffffff', opacity: 0.9, marginBottom: 8 }}>{reportDate}</Text>
        <View style={{ flexDirection: 'row', gap: 20 }}>
          <View>
            <Text style={{ fontSize: 8, color: '#ffffff', opacity: 0.7, textTransform: 'uppercase' }}>PM</Text>
            <Text style={{ fontSize: 10, color: '#ffffff', fontWeight: 600 }}>{project.pm_name || '—'}</Text>
          </View>
          <View>
            <Text style={{ fontSize: 8, color: '#ffffff', opacity: 0.7, textTransform: 'uppercase' }}>Sponsor</Text>
            <Text style={{ fontSize: 10, color: '#ffffff', fontWeight: 600 }}>{project.sponsor_name || '—'}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const VisualProgressSection = ({ report, project, riskFilter = 'active' }) => {
  const actualPct = report.actual_percent ?? null;
  const hasActualPct = actualPct !== null && actualPct !== undefined;
  const displayPct = hasActualPct ? actualPct : 0;
  const completedMilestones = project.milestones?.filter(m => m.status === 'complete').length || 0;
  const totalMilestones = project.milestones?.length || 0;
  
  const getFilteredRiskCount = () => {
    const risks = project.risks || [];
    if (riskFilter === 'active') return risks.filter(r => r.status === 'open' || r.status === 'mitigated').length;
    return risks.length;
  };
  const activeRisks = getFilteredRiskCount();
  const activeBlockers = report.blockers?.length || 0;

  const getProgressColor = (pct) => {
    if (pct >= 80) return '#22c55e';
    if (pct >= 50) return '#f59e0b';
    return '#dc2626';
  };

  return (
    <View style={{ marginBottom: 16 }} wrap={false}>
      {/* Big Progress Bar */}
      <View style={{ marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
          <Text style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Project Progress</Text>
          <Text style={{ fontSize: 20, fontWeight: 700, color: hasActualPct ? getProgressColor(displayPct) : '#64748b' }}>{hasActualPct ? `${displayPct}%` : '—'}</Text>
        </View>
        <View style={{ height: 24, backgroundColor: '#e2e8f0', borderRadius: 12 }}>
          {hasActualPct && (
            <View style={{ 
              height: 24, 
              width: `${Math.min(displayPct, 100)}%`, 
              backgroundColor: getProgressColor(displayPct), 
              borderRadius: 12 
            }} />
          )}
        </View>
      </View>

      {/* Metric Cards */}
      <View style={{ flexDirection: 'row', gap: 10 }}>
        {report.evm?.spi && (
          <View style={{ 
            flex: 1, 
            padding: 12, 
            borderRadius: 6, 
            backgroundColor: report.evm.spi >= 0.95 ? '#dcfce7' : report.evm.spi >= 0.9 ? '#fef3c7' : '#fee2e2',
            alignItems: 'center'
          }}>
            <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Schedule</Text>
            <Text style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: report.evm.spi >= 0.95 ? '#16a34a' : report.evm.spi >= 0.9 ? '#ca8a04' : '#dc2626' 
            }}>
              {report.evm.spi.toFixed(2)}
            </Text>
            <Text style={{ fontSize: 8, color: '#64748b' }}>SPI</Text>
          </View>
        )}
        {report.evm?.cpi && (
          <View style={{ 
            flex: 1, 
            padding: 12, 
            borderRadius: 6, 
            backgroundColor: report.evm.cpi >= 0.95 ? '#dcfce7' : report.evm.cpi >= 0.9 ? '#fef3c7' : '#fee2e2',
            alignItems: 'center'
          }}>
            <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Cost</Text>
            <Text style={{ 
              fontSize: 24, 
              fontWeight: 700, 
              color: report.evm.cpi >= 0.95 ? '#16a34a' : report.evm.cpi >= 0.9 ? '#ca8a04' : '#dc2626' 
            }}>
              {report.evm.cpi.toFixed(2)}
            </Text>
            <Text style={{ fontSize: 8, color: '#64748b' }}>CPI</Text>
          </View>
        )}
        <View style={{ flex: 1, padding: 12, borderRadius: 6, backgroundColor: '#f0f9ff', alignItems: 'center' }}>
          <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Milestones</Text>
          <Text style={{ fontSize: 24, fontWeight: 700, color: '#0369a1' }}>{completedMilestones}/{totalMilestones}</Text>
          <Text style={{ fontSize: 8, color: '#64748b' }}>Complete</Text>
        </View>
        <View style={{ 
          flex: 1, 
          padding: 12, 
          borderRadius: 6, 
          backgroundColor: activeRisks === 0 ? '#dcfce7' : activeRisks <= 2 ? '#fef3c7' : '#fee2e2',
          alignItems: 'center'
        }}>
          <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Risks</Text>
          <Text style={{ 
            fontSize: 24, 
            fontWeight: 700, 
            color: activeRisks === 0 ? '#16a34a' : activeRisks <= 2 ? '#ca8a04' : '#dc2626' 
          }}>{activeRisks}</Text>
          <Text style={{ fontSize: 8, color: '#64748b' }}>Active</Text>
        </View>
        <View style={{ 
          flex: 1, 
          padding: 12, 
          borderRadius: 6, 
          backgroundColor: activeBlockers === 0 ? '#dcfce7' : '#fee2e2',
          alignItems: 'center'
        }}>
          <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Blockers</Text>
          <Text style={{ 
            fontSize: 24, 
            fontWeight: 700, 
            color: activeBlockers === 0 ? '#16a34a' : '#dc2626' 
          }}>{activeBlockers}</Text>
          <Text style={{ fontSize: 8, color: '#64748b' }}>Active</Text>
        </View>
      </View>
    </View>
  );
};

const VisualChartSection = ({ chartData, project, styles }) => {
  // Show placeholder when no chart data or insufficient data points
  if (!chartData?.scurve_data?.length || chartData.scurve_data.length < 2) {
    return (
      <View style={{ marginBottom: 16, backgroundColor: '#f8fafc', borderRadius: 6, padding: 16 }} wrap={false}>
        <Text style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Performance Timeline</Text>
        <View style={{ alignItems: 'center', padding: 20 }}>
          <Text style={{ fontSize: 10, color: '#64748b', textAlign: 'center' }}>
            {chartData?.scurve_data?.length === 1 
              ? 'Performance chart will appear after your second report with EVM data.'
              : 'Performance timeline will appear after reports with EVM data are created.'}
          </Text>
        </View>
      </View>
    );
  }
  return (
    <View style={{ marginBottom: 16, backgroundColor: '#f8fafc', borderRadius: 6, padding: 12 }} wrap={false}>
      <Text style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Performance Timeline</Text>
      <View style={{ alignItems: 'center' }}>
        <SCurveChart data={chartData.scurve_data} bac={project?.bac} />
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 20, marginTop: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 12, height: 4, backgroundColor: '#3b82f6', borderRadius: 2 }} />
          <Text style={{ fontSize: 9, color: '#64748b' }}>Planned (PV)</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 12, height: 4, backgroundColor: '#22c55e', borderRadius: 2 }} />
          <Text style={{ fontSize: 9, color: '#64748b' }}>Earned (EV)</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          <View style={{ width: 12, height: 4, backgroundColor: '#f97316', borderRadius: 2 }} />
          <Text style={{ fontSize: 9, color: '#64748b' }}>Actual (AC)</Text>
        </View>
      </View>
    </View>
  );
};

const VisualMilestonesSection = ({ project, styles }) => {
  if (!project.milestones?.length) return null;
  
  const milestones = project.milestones.slice(0, 8);
  
  return (
    <View style={{ marginBottom: 16 }} wrap={false}>
      <Text style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 10 }}>Milestone Timeline</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {milestones.map((m, i) => {
          const isComplete = m.status === 'complete';
          const isLate = m.status === 'late';
          const bgColor = isComplete ? '#dcfce7' : isLate ? '#fee2e2' : '#f1f5f9';
          const borderColor = isComplete ? '#22c55e' : isLate ? '#dc2626' : '#94a3b8';
          const textColor = isComplete ? '#16a34a' : isLate ? '#dc2626' : '#334155';
          
          return (
            <View key={i} style={{ 
              width: '23%',
              padding: 8, 
              backgroundColor: bgColor, 
              borderRadius: 4,
              borderLeftWidth: 3,
              borderLeftColor: borderColor
            }}>
              <Text style={{ fontSize: 8, fontWeight: 600, color: textColor, marginBottom: 2 }}>{m.name}</Text>
              <Text style={{ fontSize: 7, color: '#64748b' }}>{formatDate(m.target_date)}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

// Main ReportPDF Component
const ReportPDF = ({
  report,
  project,
  chartData,
  colorScheme = 'slate',
  stylePack = 'modern',
  sectionOrder = [],
  sectionVisibility = {},
  template = 'standard',
  riskFilter = 'active',
  timelineView = '6_month',
  customSections = [],
}) => {
  // Early return if required data is missing
  if (!report || !project) {
    return (
      <Document>
        <Page size="A4" style={{ padding: 40 }}>
          <Text>Loading report...</Text>
        </Page>
      </Document>
    );
  }

  const scheme = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.slate;
  const styles = createStyles(scheme);

  const reportDate = report.report_date
    ? formatDate(report.report_date, 'MMMM d, yyyy')
    : '';

  const isVisible = (sectionId) => sectionVisibility[sectionId] !== false;

  // Content checks
  const hasAccomplishments = report.accomplishments?.length > 0;
  const hasUpcoming = report.upcoming?.length > 0;
  const hasBlockers = report.blockers?.length > 0;
  const getFilteredRisks = () => {
    let risks = project.risks || [];
    if (riskFilter === 'active') return risks.filter(r => r.status === 'open' || r.status === 'mitigated');
    if (riskFilter === 'notClosed') return risks.filter(r => r.status !== 'closed');
    return risks;
  };
  const hasRisks = getFilteredRisks().length > 0;
  const hasNotes = !!report.notes;
  const hasKudos = (Array.isArray(report.kudos) && report.kudos.some(k => k && (k.person || k.reason))) ||
                   (typeof report.kudos === 'string' && report.kudos.trim());

  // Executive Template - Condensed one-page summary for stakeholders
  if (template === 'executive') {
    const hasAccomplishments = sectionVisibility.accomplishments !== false && report.accomplishments?.length > 0;
    const hasUpcoming = sectionVisibility.upcoming !== false && report.upcoming?.length > 0;
    const hasBlockers = sectionVisibility.blockers !== false && report.blockers?.length > 0;
    const hasRisks = sectionVisibility.risks !== false && project.risks?.filter(r => r.status === 'open' || r.status === 'mitigated').length > 0;
    const hasNotes = sectionVisibility.notes !== false && report.notes;
    const hasKudos = sectionVisibility.kudos !== false && report.kudos?.length > 0;

    // Executive template renderSection function
    const renderExecutiveSection = (sectionId) => {
      switch (sectionId) {
        case 'header':
          return (
            <ExecutiveHeaderSection 
              key="header"
              project={project} 
              report={report} 
              reportDate={reportDate} 
              styles={styles} 
              scheme={scheme} 
            />
          );
        case 'status':
          return sectionVisibility.status !== false && report.status_summary && (
            <View key="status" style={{ marginBottom: 10, padding: 10, backgroundColor: '#f8fafc', borderRadius: 4 }} wrap={false}>
              <Text style={{ fontSize: 10, color: '#334155', lineHeight: 1.5 }}>
                {report.status_summary}
              </Text>
            </View>
          );
        case 'progress':
          return sectionVisibility.progress !== false && (
            <View key="progress" wrap={false}>
              <ProgressSection report={report} project={project} styles={styles} scheme={scheme} />
            </View>
          );
        case 'timeline':
          return sectionVisibility.timeline !== false && project.milestones?.length > 0 && (
            <View key="timeline" wrap={false}>
              <TimelineSection project={project} styles={styles} timelineView={timelineView} />
            </View>
          );
        case 'metrics':
          return sectionVisibility.metrics !== false && (
            <View key="metrics" wrap={false}>
              <MetricsSection 
                report={report} 
                project={project} 
                styles={styles} 
                scheme={scheme} 
                riskFilter={riskFilter} 
              />
            </View>
          );
        case 'chart':
          return sectionVisibility.chart !== false && chartData && (
            <View key="chart" wrap={false}>
              <ChartSection chartData={chartData} project={project} styles={styles} />
            </View>
          );
        case 'accomplishments':
          return hasAccomplishments && (
            <View key="accomplishments" style={[styles.columnSection, { borderLeftColor: '#16a34a', backgroundColor: '#f0fdf4', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#16a34a', borderBottomColor: '#16a34a' }]}>
                Key Accomplishments ({report.accomplishments.slice(0, 3).length})
              </Text>
              {report.accomplishments.slice(0, 3).map((item, i) => (
                <View key={i} style={{ marginBottom: 4, flexDirection: 'row', alignItems: 'flex-start' }}>
                  <Text style={{ fontSize: 9, color: '#16a34a', marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1, lineHeight: 1.4 }}>
                    {item.description || item}
                  </Text>
                </View>
              ))}
            </View>
          );
        case 'upcoming':
          return hasUpcoming && (
            <View key="upcoming" style={[styles.columnSection, { borderLeftColor: '#3b82f6', backgroundColor: '#eff6ff', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#3b82f6', borderBottomColor: '#3b82f6' }]}>
                Upcoming ({report.upcoming.slice(0, 3).length})
              </Text>
              {report.upcoming.slice(0, 3).map((item, i) => (
                <View key={i} style={{ marginBottom: 4, flexDirection: 'row', alignItems: 'flex-start' }}>
                  <Text style={{ fontSize: 9, color: '#3b82f6', marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1, lineHeight: 1.4 }}>
                    {item.description || item}
                  </Text>
                </View>
              ))}
            </View>
          );
        case 'blockers':
          return hasBlockers && (
            <View key="blockers" style={[styles.columnSection, { borderLeftColor: '#dc2626', backgroundColor: '#fef2f2', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#dc2626', borderBottomColor: '#dc2626' }]}>
                Blockers ({report.blockers.slice(0, 3).length})
              </Text>
              {report.blockers.slice(0, 3).map((item, i) => (
                <View key={i} style={{ marginBottom: 4, flexDirection: 'row', alignItems: 'flex-start' }}>
                  <Text style={{ fontSize: 9, color: '#dc2626', marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1, lineHeight: 1.4 }}>
                    {item.description || item}
                  </Text>
                </View>
              ))}
            </View>
          );
        case 'risks':
          return hasRisks && (
            <View key="risks" style={[styles.columnSection, { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#b45309', borderBottomColor: '#f59e0b' }]}>
                Active Risks ({project.risks.filter(r => r.status === 'open' || r.status === 'mitigated').slice(0, 3).length})
              </Text>
              {project.risks
                .filter(r => r.status === 'open' || r.status === 'mitigated')
                .slice(0, 3)
                .map((risk, i) => (
                  <View key={i} style={{ marginBottom: 4, flexDirection: 'row', alignItems: 'flex-start' }}>
                    <Text style={{ fontSize: 9, color: '#f59e0b', marginRight: 6 }}>•</Text>
                    <Text style={{ fontSize: 9, color: '#334155', flex: 1, lineHeight: 1.4 }}>
                      {risk.description}
                    </Text>
                  </View>
                ))}
            </View>
          );
        case 'decisions':
          return sectionVisibility.decisions !== false && report.decisions?.length > 0 && (
            <View key="decisions" wrap={false}>
              <ExecutiveDecisionSection report={report} styles={styles} />
            </View>
          );
        case 'milestones':
          return sectionVisibility.milestones !== false && project.milestones?.length > 0 && (
            <View key="milestones" style={[styles.fullWidthSection, { marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.sectionTitle, { fontSize: 11, marginBottom: 6 }]}>Milestones</Text>
              {project.milestones.slice(0, 5).map((m, i) => (
                <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
                  <Text style={{ fontSize: 9, color: '#334155' }}>{m.name}</Text>
                  <Text style={{ fontSize: 8, color: '#64748b' }}>{formatDate(m.target_date)}</Text>
                </View>
              ))}
            </View>
          );
        case 'budget':
          return sectionVisibility.budget !== false && report.evm && (
            <View key="budget" wrap={false}>
              <BudgetSection report={report} project={project} styles={styles} />
            </View>
          );
        case 'scope':
          return sectionVisibility.scope !== false && project.scope_summary && (
            <View key="scope" wrap={false}>
              <ScopeSection project={project} styles={styles} />
            </View>
          );
        case 'notes':
          return hasNotes && (
            <View key="notes" style={[styles.columnSection, { borderLeftColor: '#3b82f6', backgroundColor: '#f8fafc', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#3b82f6', borderBottomColor: '#3b82f6' }]}>Notes</Text>
              <Text style={{ fontSize: 9, color: '#334155', lineHeight: 1.4 }}>{report.notes}</Text>
            </View>
          );
        case 'attachments':
          const hasAttachments = sectionVisibility.attachments !== false && report.attachments?.length > 0;
          return hasAttachments && (
            <View key="attachments" style={[styles.columnSection, { borderLeftColor: '#8b5cf6', backgroundColor: '#faf5ff', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#7c3aed', borderBottomColor: '#8b5cf6' }]}>Links</Text>
              {report.attachments.map((attachment, i) => (
                <View key={attachment.id || i} style={{ marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: '#334155' }}>• {attachment.label}</Text>
                  <Link src={attachment.url} style={{ fontSize: 8, color: '#3b82f6', marginLeft: 10 }}>{attachment.url}</Link>
                </View>
              ))}
            </View>
          );
        case 'kudos':
          return hasKudos && (
            <View key="kudos" style={[styles.columnSection, { borderLeftColor: '#eab308', backgroundColor: '#fefce8', marginBottom: 8 }]} wrap={false}>
              <Text style={[styles.columnSectionTitle, { color: '#ca8a04', borderBottomColor: '#eab308' }]}>Kudos</Text>
              {report.kudos.slice(0, 3).map((item, i) => (
                <View key={i} style={{ marginBottom: 3 }}>
                  <Text style={{ fontSize: 9, color: '#334155' }}>
                    ⭐ {item.person || item.name} - {item.reason || item.description || ''}
                  </Text>
                </View>
              ))}
            </View>
          );
        default:
          return null;
      }
    };

    // Default section order for executive template
    const defaultOrder = ['header', 'status', 'progress', 'timeline', 'metrics', 'chart', 'accomplishments', 'upcoming', 'blockers', 'risks', 'decisions', 'milestones', 'budget', 'scope', 'notes', 'attachments', 'kudos'];
    const orderedSections = sectionOrder?.length > 0 ? sectionOrder : defaultOrder;

    return (
      <Document>
        <Page size="A4" style={[styles.page, { padding: 20 }]}>
          {orderedSections.map(sectionId => renderExecutiveSection(sectionId))}
          
          {/* Custom Sections */}
          <CustomSectionsComponent customSections={customSections} styles={styles} scheme={scheme} />
          
          {/* Footer - Page number only, centered */}
          <View style={{ position: 'absolute', bottom: 24, left: 40, right: 40, alignItems: 'center' }} fixed>
            <Text style={{ fontSize: 8, color: '#AEAEB2' }} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </Page>
      </Document>
    );
  }

  // Compact Template - Dense single-page for standups and printable handouts
  if (template === 'compact') {
    const compactSectionStyle = { 
      flex: 1, 
      padding: 6, 
      borderLeftWidth: 2, 
      borderRadius: 3,
      minHeight: 40
    };
    const compactTitleStyle = { 
      fontSize: 7, 
      fontWeight: 700, 
      marginBottom: 4, 
      textTransform: 'uppercase',
      letterSpacing: 0.5
    };
    const compactItemStyle = { 
      fontSize: 7, 
      color: '#334155', 
      marginBottom: 2, 
      lineHeight: 1.3 
    };
    const compactBulletStyle = { 
      fontSize: 7, 
      marginRight: 3 
    };

    // Helper to render each section
    const renderCompactSection = (sectionKey) => {
      switch(sectionKey) {
        case 'status':
          if (sectionVisibility.status === false || !report.status_summary) return null;
          return (
            <View key="status" style={{ marginBottom: 6, padding: 6, backgroundColor: '#f8fafc', borderRadius: 3 }} wrap={false}>
              <Text style={{ fontSize: 7, color: '#334155', lineHeight: 1.4 }}>{report.status_summary}</Text>
            </View>
          );
        
        case 'progress':
          if (sectionVisibility.progress === false) return null;
          return <ProgressSection key="progress" report={report} project={project} styles={styles} scheme={scheme} />;
        
        case 'timeline':
          if (sectionVisibility.timeline === false || !project.milestones?.length) return null;
          return <TimelineSection key="timeline" project={project} styles={styles} timelineView={timelineView} />;
        
        case 'metrics':
          if (sectionVisibility.metrics === false) return null;
          return <CompactMetricsSection key="metrics" report={report} project={project} riskFilter={riskFilter} />;
        
        case 'chart':
          // Show chart section - the component will display appropriate message if < 2 data points
          if (sectionVisibility.chart === false) return null;
          return <CompactChartSection key="chart" chartData={chartData} project={project} styles={styles} />;
        
        case 'accomplishments':
          if (sectionVisibility.accomplishments === false || !report.accomplishments?.length) return null;
          return (
            <View key="accomplishments" style={[compactSectionStyle, { borderLeftColor: '#16a34a', backgroundColor: '#f0fdf4' }]}>
              <Text style={[compactTitleStyle, { color: '#16a34a' }]}>ACCOMPLISHMENTS ({report.accomplishments.length})</Text>
              {report.accomplishments.map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
                  <Text style={[compactBulletStyle, { color: '#16a34a' }]}>•</Text>
                  <Text style={compactItemStyle}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        
        case 'upcoming':
          if (sectionVisibility.upcoming === false || !report.upcoming?.length) return null;
          return (
            <View key="upcoming" style={[compactSectionStyle, { borderLeftColor: '#3b82f6', backgroundColor: '#eff6ff' }]}>
              <Text style={[compactTitleStyle, { color: '#3b82f6' }]}>UPCOMING ({report.upcoming.length})</Text>
              {report.upcoming.map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
                  <Text style={[compactBulletStyle, { color: '#3b82f6' }]}>•</Text>
                  <Text style={compactItemStyle}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        
        case 'blockers':
          if (sectionVisibility.blockers === false || !report.blockers?.length) return null;
          return (
            <View key="blockers" style={[compactSectionStyle, { borderLeftColor: '#dc2626', backgroundColor: '#fef2f2' }]}>
              <Text style={[compactTitleStyle, { color: '#dc2626' }]}>BLOCKERS ({report.blockers.length})</Text>
              {report.blockers.map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
                  <Text style={[compactBulletStyle, { color: '#dc2626' }]}>•</Text>
                  <Text style={compactItemStyle}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        
        case 'risks':
          const filteredRisks = (project.risks || []).filter(r => r.status === 'open' || r.status === 'mitigated');
          if (sectionVisibility.risks === false || !filteredRisks.length) return null;
          return (
            <View key="risks" style={[compactSectionStyle, { borderLeftColor: '#f59e0b', backgroundColor: '#fffbeb' }]}>
              <Text style={[compactTitleStyle, { color: '#b45309' }]}>RISKS ({filteredRisks.length})</Text>
              {filteredRisks.map((risk, i) => (
                <View key={i} style={{ marginBottom: 3 }}>
                  <Text style={compactItemStyle}>{risk.description}</Text>
                  <Text style={{ fontSize: 6, color: '#92400e' }}>P: {risk.probability?.charAt(0).toUpperCase()} | I: {risk.impact?.charAt(0).toUpperCase()}</Text>
                </View>
              ))}
            </View>
          );
        
        case 'decisions':
          if (sectionVisibility.decisions === false || !report.decisions?.length) return null;
          return (
            <View key="decisions" style={[compactSectionStyle, { borderLeftColor: '#7c3aed', backgroundColor: '#f5f3ff' }]}>
              <Text style={[compactTitleStyle, { color: '#7c3aed' }]}>DECISIONS ({report.decisions.length})</Text>
              {report.decisions.map((item, i) => (
                <View key={i} style={{ marginBottom: 3 }}>
                  <Text style={compactItemStyle}>{item.description || item}</Text>
                  {item.needed_by && <Text style={{ fontSize: 6, color: '#64748b' }}>By: {formatDate(item.needed_by)}</Text>}
                </View>
              ))}
            </View>
          );
        
        case 'milestones':
          if (sectionVisibility.milestones === false || !project.milestones?.length) return null;
          return (
            <View key="milestones" style={[compactSectionStyle, { borderLeftColor: '#6366f1', backgroundColor: '#eef2ff' }]}>
              <Text style={[compactTitleStyle, { color: '#6366f1' }]}>MILESTONES ({project.milestones.length})</Text>
              {project.milestones.map((m, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 2 }}>
                  <Text style={[compactItemStyle, { width: 100 }]}>{m.name}</Text>
                  <Text style={{ fontSize: 6, color: '#64748b' }}>{formatDate(m.target_date)}</Text>
                </View>
              ))}
            </View>
          );
        
        case 'budget':
          if (sectionVisibility.budget === false || !report.evm) return null;
          return (
            <View key="budget" style={[compactSectionStyle, { borderLeftColor: '#059669', backgroundColor: '#ecfdf5' }]}>
              <Text style={[compactTitleStyle, { color: '#059669' }]}>BUDGET</Text>
              <View style={{ flexDirection: 'row', marginBottom: 2 }}>
                <Text style={[compactItemStyle, { width: 70 }]}>PV (Planned)</Text>
                <Text style={compactItemStyle}>${report.pv?.toLocaleString() || '—'}</Text>
              </View>
              <View style={{ flexDirection: 'row', marginBottom: 2 }}>
                <Text style={[compactItemStyle, { width: 70 }]}>EV (Earned)</Text>
                <Text style={compactItemStyle}>${report.ev?.toLocaleString() || '—'}</Text>
              </View>
              <View style={{ flexDirection: 'row', marginBottom: 2 }}>
                <Text style={[compactItemStyle, { width: 70 }]}>AC (Actual)</Text>
                <Text style={compactItemStyle}>${report.ac?.toLocaleString() || '—'}</Text>
              </View>
            </View>
          );
        
        case 'scope':
          if (sectionVisibility.scope === false || !project.scope_summary) return null;
          return (
            <View key="scope" style={[compactSectionStyle, { borderLeftColor: '#64748b', backgroundColor: '#f8fafc' }]}>
              <Text style={[compactTitleStyle, { color: '#64748b' }]}>SCOPE</Text>
              <Text style={compactItemStyle}>{project.scope_summary}</Text>
            </View>
          );
        
        case 'notes':
          if (sectionVisibility.notes === false || !report.notes) return null;
          return (
            <View key="notes" style={[compactSectionStyle, { borderLeftColor: '#3b82f6', backgroundColor: '#f8fafc' }]}>
              <Text style={[compactTitleStyle, { color: '#3b82f6' }]}>NOTES</Text>
              <Text style={compactItemStyle}>{report.notes}</Text>
            </View>
          );
        
        case 'attachments':
          if (sectionVisibility.attachments === false || !report.attachments?.length) return null;
          return (
            <View key="attachments" style={[compactSectionStyle, { borderLeftColor: '#8b5cf6', backgroundColor: '#faf5ff' }]}>
              <Text style={[compactTitleStyle, { color: '#7c3aed' }]}>LINKS ({report.attachments.length})</Text>
              {report.attachments.map((attachment, i) => (
                <View key={attachment.id || i} style={{ marginBottom: 2 }}>
                  <Text style={compactItemStyle}>• {attachment.label}</Text>
                  <Link src={attachment.url} style={{ fontSize: 6, color: '#3b82f6', marginLeft: 8 }}>{attachment.url}</Link>
                </View>
              ))}
            </View>
          );
        
        case 'kudos':
          if (sectionVisibility.kudos === false || !report.kudos?.length) return null;
          return (
            <View key="kudos" style={[compactSectionStyle, { borderLeftColor: '#eab308', backgroundColor: '#fefce8' }]}>
              <Text style={[compactTitleStyle, { color: '#ca8a04' }]}>KUDOS ({report.kudos.length})</Text>
              {report.kudos.map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', marginBottom: 2 }}>
                  <Text style={[compactBulletStyle, { color: '#eab308' }]}>★</Text>
                  <Text style={compactItemStyle}>{item.person || item.name}{item.reason ? ` - ${item.reason}` : ''}</Text>
                </View>
              ))}
            </View>
          );
        
        default:
          return null;
      }
    };

    // All sections that can be reordered
    const allSections = ['header', 'status', 'progress', 'timeline', 'metrics', 'chart', 'accomplishments', 'upcoming', 'blockers', 'risks', 'decisions', 'milestones', 'budget', 'scope', 'notes', 'attachments', 'kudos'];
    const orderedSections = sectionOrder?.length > 0 ? sectionOrder : allSections;
    
    // Card sections that render in rows of 3
    const cardSections = ['accomplishments', 'upcoming', 'blockers', 'risks', 'decisions', 'milestones', 'budget', 'scope', 'notes', 'attachments', 'kudos'];
    // Top sections that render full width
    const topSections = ['header', 'status', 'progress', 'timeline', 'metrics', 'chart'];
    
    // Separate ordered sections into top and card sections while preserving order
    const orderedTopSections = orderedSections.filter(s => topSections.includes(s));
    const orderedCardSections = orderedSections.filter(s => cardSections.includes(s));
    
    const rows = [];
    let currentRow = [];
    orderedCardSections.forEach(sectionKey => {
      const rendered = renderCompactSection(sectionKey);
      if (rendered) {
        currentRow.push(rendered);
        if (currentRow.length === 3) {
          rows.push([...currentRow]);
          currentRow = [];
        }
      }
    });
    if (currentRow.length > 0) {
      rows.push([...currentRow]);
    }

    return (
      <Document>
        <Page size="A4" style={[styles.page, { padding: 12 }]}>
          {/* Top sections in order */}
          {orderedTopSections.map(sectionKey => {
            if (sectionKey === 'header') {
              return (
                <CompactHeaderSection 
                  key="header"
                  project={project} 
                  report={report} 
                  reportDate={reportDate} 
                  styles={styles} 
                  scheme={scheme} 
                />
              );
            }
            return renderCompactSection(sectionKey);
          })}
          
          {/* Card sections in rows of 3 */}
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={{ flexDirection: 'row', gap: 6, marginBottom: 6 }} wrap={false}>
              {row}
            </View>
          ))}
          
          {/* Custom Sections */}
          <CustomSectionsComponent customSections={customSections} styles={styles} scheme={scheme} />
          
          {/* Footer - Page number only, centered */}
          <View style={{ position: 'absolute', bottom: 24, left: 40, right: 40, alignItems: 'center' }} fixed>
            <Text style={{ fontSize: 8, color: '#AEAEB2' }} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </Page>
      </Document>
    );
  }

  // Visual Template - Dashboard-style with big graphics
  if (template === 'visual') {
    const hasAccomplishments = sectionVisibility.accomplishments !== false && report.accomplishments?.length > 0;
    const hasUpcoming = sectionVisibility.upcoming !== false && report.upcoming?.length > 0;
    const hasBlockers = sectionVisibility.blockers !== false && report.blockers?.length > 0;
    const hasRisks = sectionVisibility.risks !== false && project.risks?.filter(r => r.status === 'open' || r.status === 'mitigated').length > 0;
    const hasDecisions = sectionVisibility.decisions !== false && report.decisions?.length > 0;
    const hasNotes = sectionVisibility.notes !== false && report.notes;
    const hasKudos = sectionVisibility.kudos !== false && report.kudos?.length > 0;
    const hasBudget = sectionVisibility.budget !== false && (report.pv || report.ev || report.ac);
    const hasScope = sectionVisibility.scope !== false && project.scope_summary;

    // Visual template renderSection function
    const renderVisualSection = (sectionId) => {
      switch (sectionId) {
        case 'header':
          return (
            <VisualHeaderSection 
              key="header"
              project={project} 
              report={report} 
              reportDate={reportDate} 
              styles={styles} 
              scheme={scheme} 
            />
          );
        case 'status':
          return sectionVisibility.status !== false && report.status_summary && (
            <View key="status" style={{ marginBottom: 16, padding: 12, backgroundColor: '#f8fafc', borderRadius: 6 }} wrap={false}>
              <Text style={{ fontSize: 11, color: '#334155', lineHeight: 1.5 }}>{report.status_summary}</Text>
            </View>
          );
        case 'progress':
          return sectionVisibility.progress !== false && (
            <View key="progress" wrap={false}>
              <ProgressSection report={report} project={project} styles={styles} scheme={scheme} />
            </View>
          );
        case 'timeline':
          return sectionVisibility.timeline !== false && project.milestones?.length > 0 && (
            <View key="timeline" wrap={false}>
              <TimelineSection project={project} styles={styles} timelineView={timelineView} />
            </View>
          );
        case 'metrics':
          return sectionVisibility.metrics !== false && (
            <View key="metrics">
              <VisualProgressSection report={report} project={project} riskFilter={riskFilter} />
            </View>
          );
        case 'chart':
          // Show chart section - the component will display appropriate message if < 2 data points
          return sectionVisibility.chart !== false && (
            <View key="chart">
              <VisualChartSection chartData={chartData} project={project} styles={styles} />
            </View>
          );
        case 'milestones':
          return sectionVisibility.milestones !== false && project.milestones?.length > 0 && (
            <View key="milestones">
              <VisualMilestonesSection project={project} styles={styles} />
            </View>
          );
        case 'accomplishments':
          return hasAccomplishments && (
            <View key="accomplishments" style={{ padding: 12, backgroundColor: '#f0fdf4', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#22c55e', marginBottom: 12 }} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <CheckCircleIcon size={14} color="#16a34a" />
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginLeft: 6 }}>
                  Accomplishments ({report.accomplishments.length})
                </Text>
              </View>
              {report.accomplishments.slice(0, 4).map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: '#16a34a', marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1 }}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        case 'upcoming':
          return hasUpcoming && (
            <View key="upcoming" style={{ padding: 12, backgroundColor: '#eff6ff', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#3b82f6', marginBottom: 12 }} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <TargetIcon size={14} color="#3b82f6" />
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', marginLeft: 6 }}>
                  Upcoming ({report.upcoming.length})
                </Text>
              </View>
              {report.upcoming.slice(0, 4).map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: '#3b82f6', marginRight: 6 }}>•</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1 }}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        case 'blockers':
          return hasBlockers && (
            <View key="blockers" style={{ padding: 12, backgroundColor: '#fef2f2', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#dc2626', marginBottom: 12 }} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <XCircleIcon size={14} color="#dc2626" />
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginLeft: 6 }}>
                  Blockers ({report.blockers.length})
                </Text>
              </View>
              {report.blockers.slice(0, 4).map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: '#dc2626', marginRight: 6 }}>✕</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1 }}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        case 'risks':
          return hasRisks && (
            <View key="risks" style={{ padding: 12, backgroundColor: '#fffbeb', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#f59e0b', marginBottom: 12 }} wrap={false}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 8 }}>
                Risks ({project.risks.filter(r => r.status === 'open' || r.status === 'mitigated').length})
              </Text>
              {project.risks
                .filter(r => r.status === 'open' || r.status === 'mitigated')
                .slice(0, 4)
                .map((risk, i) => (
                  <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                    <Text style={{ fontSize: 9, color: '#f59e0b', marginRight: 6 }}>▲</Text>
                    <Text style={{ fontSize: 9, color: '#334155', flex: 1 }}>{risk.description}</Text>
                  </View>
                ))}
            </View>
          );
        case 'decisions':
          return hasDecisions && (
            <View key="decisions" style={{ padding: 12, backgroundColor: '#f5f3ff', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#7c3aed', marginBottom: 12 }} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 14, color: '#7c3aed', marginRight: 6 }}>?</Text>
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>
                  Decisions Needed ({report.decisions.length})
                </Text>
              </View>
              {report.decisions.slice(0, 3).map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: '#7c3aed', marginRight: 6 }}>?</Text>
                  <Text style={{ fontSize: 9, color: '#334155', flex: 1 }}>{item.description || item}</Text>
                </View>
              ))}
            </View>
          );
        case 'budget':
          return hasBudget && (
            <View key="budget" style={{ padding: 12, backgroundColor: '#ecfdf5', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#059669', marginBottom: 12 }} wrap={false}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: '#059669', marginBottom: 8 }}>Budget Overview</Text>
              <View style={{ flexDirection: 'row', gap: 20 }}>
                <View>
                  <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase' }}>Planned (PV)</Text>
                  <Text style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>${report.pv?.toLocaleString() || '—'}</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase' }}>Earned (EV)</Text>
                  <Text style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>${report.ev?.toLocaleString() || '—'}</Text>
                </View>
                <View>
                  <Text style={{ fontSize: 8, color: '#64748b', textTransform: 'uppercase' }}>Actual (AC)</Text>
                  <Text style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>${report.ac?.toLocaleString() || '—'}</Text>
                </View>
              </View>
            </View>
          );
        case 'scope':
          return hasScope && (
            <View key="scope" style={{ padding: 12, backgroundColor: '#f8fafc', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#64748b', marginBottom: 12 }} wrap={false}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Scope</Text>
              <Text style={{ fontSize: 9, color: '#334155', lineHeight: 1.4 }}>{project.scope_summary}</Text>
            </View>
          );
        case 'notes':
          return hasNotes && (
            <View key="notes" style={{ padding: 12, backgroundColor: '#f8fafc', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#3b82f6', marginBottom: 12 }} wrap={false}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', marginBottom: 6 }}>Notes</Text>
              <Text style={{ fontSize: 9, color: '#334155', lineHeight: 1.4 }}>{report.notes}</Text>
            </View>
          );
        case 'attachments':
          const hasAttachmentsVisual = sectionVisibility.attachments !== false && report.attachments?.length > 0;
          return hasAttachmentsVisual && (
            <View key="attachments" style={{ padding: 12, backgroundColor: '#faf5ff', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#8b5cf6', marginBottom: 12 }} wrap={false}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 6 }}>Links ({report.attachments.length})</Text>
              {report.attachments.map((attachment, i) => (
                <View key={attachment.id || i} style={{ marginBottom: 4 }}>
                  <Text style={{ fontSize: 9, color: '#334155' }}>• {attachment.label}</Text>
                  <Link src={attachment.url} style={{ fontSize: 8, color: '#3b82f6', marginLeft: 10 }}>{attachment.url}</Link>
                </View>
              ))}
            </View>
          );
        case 'kudos':
          return hasKudos && (
            <View key="kudos" style={{ padding: 12, backgroundColor: '#fefce8', borderRadius: 6, borderLeftWidth: 4, borderLeftColor: '#eab308', marginBottom: 12 }} wrap={false}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 14, color: '#eab308', marginRight: 6 }}>★</Text>
                <Text style={{ fontSize: 11, fontWeight: 700, color: '#ca8a04' }}>
                  Kudos ({report.kudos.length})
                </Text>
              </View>
              {report.kudos.slice(0, 3).map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', marginBottom: 3 }}>
                  <Text style={{ fontSize: 9, color: '#eab308', marginRight: 6 }}>★</Text>
                  <Text style={{ fontSize: 9, color: '#334155' }}>{item.person || item.name}{item.reason ? ` - ${item.reason}` : ''}</Text>
                </View>
              ))}
            </View>
          );
        default:
          return null;
      }
    };

    // Default section order for visual template
    const defaultOrder = ['header', 'status', 'progress', 'timeline', 'metrics', 'chart', 'milestones', 'accomplishments', 'upcoming', 'blockers', 'risks', 'decisions', 'budget', 'scope', 'notes', 'attachments', 'kudos'];
    const orderedSections = sectionOrder?.length > 0 ? sectionOrder : defaultOrder;

    return (
      <Document>
        <Page size="A4" style={[styles.page, { padding: 24 }]}>
          {orderedSections.map(sectionId => renderVisualSection(sectionId))}
          
          {/* Custom Sections */}
          <CustomSectionsComponent customSections={customSections} styles={styles} scheme={scheme} />
          
          {/* Footer - Page number only, centered */}
          <View style={{ position: 'absolute', bottom: 24, left: 24, right: 24, alignItems: 'center' }} fixed>
            <Text style={{ fontSize: 8, color: '#AEAEB2' }} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </Page>
      </Document>
    );
  }

  // Standard Template
  if (template === 'standard') {
    // Determine layout for paired sections
    const showAccomplishments = isVisible('accomplishments') && hasAccomplishments;
    const showUpcoming = isVisible('upcoming') && hasUpcoming;
    const showBlockers = isVisible('blockers') && hasBlockers;
    const showRisks = isVisible('risks') && hasRisks;
    const showNotes = isVisible('notes') && hasNotes;
    const showKudos = isVisible('kudos') && hasKudos;

    // Render individual section based on ID
    const renderSection = (sectionId) => {
      switch (sectionId) {
        case 'header':
          return isVisible('header') && <HeaderSection key="header" project={project} report={report} reportDate={reportDate} styles={styles} scheme={scheme} />;
        case 'timeline':
          return isVisible('timeline') && <TimelineSection key="timeline" project={project} styles={styles} timelineView={timelineView} />;
        case 'progress':
          return isVisible('progress') && <ProgressSection key="progress" report={report} project={project} styles={styles} scheme={scheme} />;
        case 'status':
          return isVisible('status') && <StatusSection key="status" report={report} styles={styles} />;
        case 'metrics':
          return isVisible('metrics') && <MetricsSection key="metrics" report={report} project={project} styles={styles} scheme={scheme} riskFilter={riskFilter} />;
        case 'chart':
          return isVisible('chart') && <ChartSection key="chart" chartData={chartData} project={project} styles={styles} />;
        case 'accomplishments':
          return showAccomplishments && <AccomplishmentsSection key="accomplishments" report={report} styles={styles} inColumn={false} />;
        case 'upcoming':
          return showUpcoming && <UpcomingSection key="upcoming" report={report} styles={styles} scheme={scheme} inColumn={false} />;
        case 'blockers':
          return showBlockers && <BlockersSection key="blockers" report={report} styles={styles} inColumn={false} />;
        case 'risks':
          return showRisks && <RisksSection key="risks" project={project} styles={styles} scheme={scheme} riskFilter={riskFilter} inColumn={false} />;
        case 'milestones':
          return isVisible('milestones') && <MilestonesSection key="milestones" project={project} styles={styles} scheme={scheme} />;
        case 'budget':
          return isVisible('budget') && <BudgetSection key="budget" report={report} project={project} styles={styles} />;
        case 'decisions':
          return isVisible('decisions') && <DecisionsSection key="decisions" report={report} styles={styles} />;
        case 'scope':
          return isVisible('scope') && <ScopeSection key="scope" project={project} styles={styles} />;
        case 'notes':
          return showNotes && <NotesSection key="notes" report={report} styles={styles} inColumn={false} />;
        case 'attachments':
          return isVisible('attachments') && report.attachments?.length > 0 && <AttachmentsSection key="attachments" report={report} styles={styles} inColumn={false} />;
        case 'kudos':
          return showKudos && <KudosSection key="kudos" report={report} styles={styles} scheme={scheme} inColumn={false} />;
        default:
          return null;
      }
    };

    // Default section order (matches REPORT_SECTIONS in constants.js)
    const defaultOrder = ['header', 'status', 'progress', 'timeline', 'metrics', 'chart', 'accomplishments', 'upcoming', 'blockers', 'risks', 'decisions', 'budget', 'milestones', 'scope', 'notes', 'attachments', 'kudos'];
    const orderedSections = sectionOrder?.length > 0 ? sectionOrder : defaultOrder;

    return (
      <Document
        title={`${project.name} - Status Report - ${reportDate}`}
        author={project.pm_name || ''}
        subject={`Project Status Report for ${project.name}`}
      >
        <Page size="A4" style={styles.page}>
          {orderedSections.map(sectionId => renderSection(sectionId))}
          
          {/* Custom Sections */}
          <CustomSectionsComponent customSections={customSections} styles={styles} scheme={scheme} />

          {/* Footer - Page number only, centered */}
          <View style={{ position: 'absolute', bottom: 24, left: 40, right: 40, alignItems: 'center' }} fixed>
            <Text style={{ fontSize: 8, color: '#AEAEB2' }} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </Page>
      </Document>
    );
  }

  // Fallback for other templates
  return (
    <Document
      title={`${project.name} - Status Report - ${reportDate}`}
      author={project.pm_name || ''}
      subject={`Project Status Report for ${project.name}`}
    >
      <Page size="A4" style={styles.page}>
        {isVisible('header') && <HeaderSection project={project} report={report} reportDate={reportDate} styles={styles} scheme={scheme} />}
        {isVisible('timeline') && <TimelineSection project={project} styles={styles} timelineView={timelineView} />}
        {isVisible('progress') && <ProgressSection report={report} project={project} styles={styles} scheme={scheme} />}
        {isVisible('status') && <StatusSection report={report} styles={styles} />}
        {isVisible('metrics') && <MetricsSection report={report} project={project} styles={styles} scheme={scheme} riskFilter={riskFilter} />}
        {isVisible('chart') && <ChartSection chartData={chartData} project={project} styles={styles} />}
        {isVisible('accomplishments') && <AccomplishmentsSection report={report} styles={styles} />}
        {isVisible('upcoming') && <UpcomingSection report={report} styles={styles} scheme={scheme} />}
        {isVisible('blockers') && <BlockersSection report={report} styles={styles} />}
        {isVisible('risks') && <RisksSection project={project} styles={styles} scheme={scheme} riskFilter={riskFilter} />}
        {isVisible('milestones') && <MilestonesSection project={project} styles={styles} scheme={scheme} />}
        {isVisible('budget') && <BudgetSection report={report} project={project} styles={styles} />}
        {isVisible('decisions') && <DecisionsSection report={report} styles={styles} />}
        {isVisible('scope') && <ScopeSection project={project} styles={styles} />}
        {isVisible('notes') && <NotesSection report={report} styles={styles} />}
        {isVisible('kudos') && <KudosSection report={report} styles={styles} scheme={scheme} />}
        
        {/* Custom Sections */}
        <CustomSectionsComponent customSections={customSections} styles={styles} scheme={scheme} />

        {/* Footer - Page number only, centered */}
        <View style={{ position: 'absolute', bottom: 24, left: 40, right: 40, alignItems: 'center' }} fixed>
          <Text style={{ fontSize: 8, color: '#AEAEB2' }} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default ReportPDF;
